import React, { useEffect, useMemo, useState, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import {
  ArrowLeft, Wallet, TrendingDown, Lock, Plus, AlertTriangle, FileStack, ClipboardList,
  Users, UserPlus, Scale, CircleDot, ChevronRight, CalendarDays, History, Flag, GitBranch,
  HardHat, ChevronsDownUp, ChevronsUpDown, Check, X, Trash2, ShieldCheck, Clock,
  PiggyBank, Unlock, CheckCircle2, Rocket, Receipt, Gauge, LayoutDashboard,
} from 'lucide-react';
import api from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { fmtMoneyLabel, fmtDate } from '../lib/format';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Checkbox } from '../components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { NumberStepper } from '../components/NumberStepper';
import { CostCentreOpening } from '../components/workspace/CostCentreOpening';
import { InvoicePlan } from '../components/workspace/InvoicePlan';
import { CostControl } from '../components/workspace/CostControl';
import { ProjectOverview } from '../components/workspace/ProjectOverview';
import { useIsMobile } from '../hooks/use-media-query';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { LineChart, Line, XAxis, YAxis, ResponsiveContainer, Tooltip as RTooltip, CartesianGrid, Legend } from 'recharts';
import { cn } from '../lib/utils';

// Budget-health buckets — parity with backend (On Track < 80% · At Risk 80–100% · Over Budget > 100%).
const HEALTH = {
  on_track: { label: 'On Track', tone: 'success' },
  at_risk: { label: 'At Risk', tone: 'warning' },
  over_budget: { label: 'Over Budget', tone: 'danger' },
};

function HealthPill({ health, testid }) {
  const h = HEALTH[health] || HEALTH.on_track;
  return (
    <span data-testid={testid} className={cn('inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[11px] font-medium',
      `bg-[hsl(var(--${h.tone})/0.12)] text-[hsl(var(--${h.tone}))]`)}>
      <CircleDot size={11} />{h.label}
    </span>
  );
}

// Activity-timeline event presentation (C4).
const EVENT_META = {
  booking: { icon: ClipboardList, tone: 'teal', label: 'Cost booked' },
  revision: { icon: GitBranch, tone: 'warning', label: 'Revision' },
  execution: { icon: HardHat, tone: 'copper', label: 'Execution certified' },
  team: { icon: Users, tone: 'brand', label: 'Team updated' },
  status: { icon: Flag, tone: 'success', label: 'Status change' },
};

const fmtQty = (n) => (n == null ? '—' : (Number.isInteger(n) ? String(n) : Number(n).toFixed(2)));

// Bookings (C6) and certified execution (C8) are whole-number quantities only.
const fmtInt = (n) => (n == null ? '—' : String(Math.round(Number(n))));

// Group flat lines into the Cost BOQ hierarchy: Sale Item (S-01, S-02 …) → cost
// category (Material, Labour …) → lines. Sale items follow the BOQ order.
function groupBySaleItem(lines, siOrder, descByItem) {
  const map = {};
  (lines || []).forEach((l) => {
    const item = l.itemNo || '—';
    if (!map[item]) map[item] = { itemNo: item, description: descByItem[item] || '', cats: {}, catOrder: [], lines: [] };
    const m = map[item];
    m.lines.push(l);
    if (!m.cats[l.category]) { m.cats[l.category] = { name: l.category, lines: [] }; m.catOrder.push(l.category); }
    m.cats[l.category].lines.push(l);
  });
  const seen = new Set();
  const out = [];
  (siOrder || []).forEach((it) => { if (map[it]) { out.push(map[it]); seen.add(it); } });
  Object.keys(map).forEach((it) => { if (!seen.has(it)) out.push(map[it]); });
  return out;
}
const sumBy = (arr, f) => (arr || []).reduce((s, x) => s + (Number(f(x)) || 0), 0);

// C8 — productivity flag presentation.
const EXEC_FLAG = {
  productive: { label: 'Productive', tone: 'success' },
  behind: { label: 'Behind schedule', tone: 'warning' },
  idling: { label: 'Under-producing / Idling', tone: 'danger' },
};

// C9 — revision status presentation.
const REV_TONE = { Pending: 'warning', Approved: 'success', Rejected: 'danger' };

// C9 — derive per-stage status for the horizontal approval stepper.
// Stage 0 is the initiator (who raised it); stages 1..n are sequential approvers.
function buildStepper(rev, stages) {
  const decByKey = {};
  (rev.decisions || []).forEach((d) => { decByKey[d.stageKey] = d; });
  return (stages || []).map((st, i) => {
    if (i === 0) return { stage: st, status: 'raised', by: rev.raisedBy, at: rev.createdAt };
    const d = decByKey[st.key];
    if (d) return { stage: st, status: d.decision === 'Approved' ? 'approved' : 'rejected', by: d.by, at: d.at, note: d.note, override: d.override };
    if (rev.status === 'Pending' && i === rev.stageIndex) return { stage: st, status: 'current' };
    return { stage: st, status: 'upcoming' };
  });
}

const STEP_META = {
  raised: { tone: 'brand', Icon: GitBranch },
  approved: { tone: 'success', Icon: Check },
  rejected: { tone: 'danger', Icon: X },
  current: { tone: 'warning', Icon: Clock },
  upcoming: { tone: 'muted-foreground', Icon: CircleDot },
};


export default function BudgetDetail({ view = 'project' }) {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const isAdmin = user?.groupName === 'Admin';

  const isCosting = view === 'costing';
  const basePath = isCosting ? '/costing' : '/budget';
  const backLabel = isCosting ? 'Back to Cost Centres' : 'Back to Project Tracker';

  const [data, setData] = useState(null);
  const [tab, setTab] = useState(isCosting ? 'opening' : 'overview');
  const isMobile = useIsMobile();
  const tabsWrapRef = useRef(null);
  const touchRef = useRef(null);

  // Ordered visible tabs (matches the TabsList render order) for swipe navigation.
  const tabOrder = useMemo(
    () => (isCosting
      ? ['opening', 'tracker', 'invoice', 'costcontrol', 'revisions', 'savings', 'history', 'boq']
      : ['overview', 'execution', 'history', 'boq']),
    [isCosting],
  );
  const goTab = useCallback((dir) => {
    setTab((cur) => {
      const i = tabOrder.indexOf(cur);
      if (i === -1) return cur;
      return tabOrder[Math.min(tabOrder.length - 1, Math.max(0, i + dir))];
    });
  }, [tabOrder]);
  const onTabTouchStart = (e) => {
    const t = e.touches[0];
    touchRef.current = { x: t.clientX, y: t.clientY };
  };
  const onTabTouchEnd = (e) => {
    const s = touchRef.current; touchRef.current = null;
    if (!s) return;
    const t = e.changedTouches[0];
    const dx = t.clientX - s.x; const dy = t.clientY - s.y;
    // Only treat clearly horizontal, deliberate swipes as tab changes.
    if (Math.abs(dx) < 60 || Math.abs(dx) < Math.abs(dy) * 1.5) return;
    goTab(dx < 0 ? 1 : -1); // swipe left → next tab, right → previous
  };
  // Keep the active tab visible in the scrollable tab bar.
  useEffect(() => {
    const el = tabsWrapRef.current?.querySelector(`[data-testid="tab-${tab}"]`);
    if (el?.scrollIntoView) el.scrollIntoView({ inline: 'center', block: 'nearest', behavior: 'smooth' });
  }, [tab]);
  const [siOpen, setSiOpen] = useState({});          // C6 — tracker Sale-Item sections (open by default)
  const [execSiOpen, setExecSiOpen] = useState({});  // C8 — execution Sale-Item sections (open by default)
  const [boqExpanded, setBoqExpanded] = useState({}); // baseline BOQ categories
  const [history, setHistory] = useState(null);
  const cur = data?.project?.currency || 'AED';
  const money = (f) => fmtMoneyLabel(f, cur);

  const load = useCallback(async () => {
    try { const { data } = await api.get(`/budget/projects/${id}`); setData(data); }
    catch (e) { toast.error(e.response?.data?.detail || 'Could not load project budget'); navigate(basePath); }
  }, [id, navigate, basePath]);
  useEffect(() => { load(); }, [load]);

  const loadHistory = useCallback(async () => {
    try { const { data: h } = await api.get(`/budget/projects/${id}/history`); setHistory(h); }
    catch { setHistory({ events: [], baselineBudget: 0, summary: {} }); }
  }, [id]);
  useEffect(() => { if (tab === 'history' && history === null) loadHistory(); }, [tab, history, loadHistory]);

  // ---- C8 Site execution ----
  const [execData, setExecData] = useState(null);
  const [execEdits, setExecEdits] = useState({});
  const [deliveredEdits, setDeliveredEdits] = useState({});
  const loadExec = useCallback(async () => {
    try { const { data: x } = await api.get(`/budget/projects/${id}/execution`); setExecData(x); setExecEdits({}); setDeliveredEdits({}); }
    catch { setExecData({ metrics: { lines: [], categories: [] }, trend: [] }); }
  }, [id]);
  useEffect(() => { if (tab === 'execution' && execData === null) loadExec(); }, [tab, execData, loadExec]);

  const refresh = useCallback(() => { setHistory(null); setExecData(null); setSavingsData(null); setRevData(null); load(); }, [load]);

  // Cost Centre Opening — Costing pushes the project to the Project team, enabling
  // Site Execution (blocked until opened). P2 will gate this on departmental sign-offs.
  const [opening, setOpening] = useState(false);
  const openCostCentre = async () => {
    setOpening(true);
    try {
      const { data: r } = await api.post(`/projects/${id}/open-cost-centre`);
      toast.success(`Cost centre opened — pushed to the Project team by ${r.openedBy}`);
      refresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not open the cost centre');
    } finally { setOpening(false); }
  };

  const submitExecution = async () => {
    const exEntries = Object.entries(execEdits).filter(([, v]) => v !== '');
    const delEntries = Object.entries(deliveredEdits).filter(([, v]) => v !== '');
    const bad = [...exEntries, ...delEntries].filter(([, v]) => !/^\d+$/.test(String(v)));
    if (bad.length > 0) { toast.error('Quantities must be whole numbers (no decimals).'); return; }
    if (exEntries.length === 0 && delEntries.length === 0) { toast.error('Enter at least one quantity to save'); return; }
    const exLines = exEntries.map(([lineId, v]) => ({ lineId, executedQty: parseInt(v, 10) }));
    const delLines = delEntries.map(([lineId, v]) => ({ lineId, deliveredQty: parseInt(v, 10) }));
    try {
      if (exLines.length > 0) await api.post(`/budget/projects/${id}/execution`, { lines: exLines });
      if (delLines.length > 0) await api.post(`/budget/projects/${id}/delivered`, { lines: delLines });
      toast.success('Site execution saved'); loadExec(); load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not save site execution'); }
  };

  // ---- C9 Budget revisions ----
  const [revData, setRevData] = useState(null);
  const loadRevisions = useCallback(async () => {
    try { const { data: r } = await api.get(`/budget/projects/${id}/revisions`); setRevData(r); }
    catch { setRevData({ revisions: [], stages: [] }); }
  }, [id]);
  useEffect(() => { if (tab === 'revisions' && revData === null) loadRevisions(); }, [tab, revData, loadRevisions]);

  const [revOpen, setRevOpen] = useState(false);
  const [revForm, setRevForm] = useState({ reason: '', lines: [] });
  const [revPick, setRevPick] = useState({ lineId: '', newQty: '', newRate: '' });
  const openRevision = () => { setRevForm({ reason: '', lines: [] }); setRevPick({ lineId: '', newQty: '', newRate: '' }); setRevOpen(true); };
  const addRevLine = () => {
    const bl = (budget?.lines || []).find((l) => l.lineId === revPick.lineId);
    if (!bl) { toast.error('Select a line'); return; }
    const newQty = revPick.newQty === '' ? bl.budgetQty : Number(revPick.newQty);
    const newRate = revPick.newRate === '' ? bl.rate / 100 : Number(revPick.newRate);
    if (!(newQty > 0) || !(newRate > 0)) { toast.error('Quantity and rate must be greater than zero'); return; }
    if (newQty === bl.budgetQty && Math.round(newRate * 100) === bl.rate) { toast.error('Enter a different qty or rate'); return; }
    setRevForm((f) => ({ ...f, lines: [...f.lines.filter((x) => x.lineId !== bl.lineId), { lineId: bl.lineId, newQty, newRate }] }));
    setRevPick({ lineId: '', newQty: '', newRate: '' });
  };
  const removeRevLine = (lid) => setRevForm((f) => ({ ...f, lines: f.lines.filter((x) => x.lineId !== lid) }));
  const submitRevision = async () => {
    if (!revForm.reason.trim()) { toast.error('A reason is required'); return; }
    if (revForm.lines.length === 0) { toast.error('Add at least one line change'); return; }
    try {
      await api.post(`/budget/projects/${id}/revisions`, revForm);
      toast.success('Revision raised'); setRevOpen(false); setRevData(null); load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not raise revision'); }
  };
  const decideRevision = async (rev, decision, approveAll = false) => {
    const note = decision === 'Rejected' ? (window.prompt('Reason for rejection (optional):') ?? '') : '';
    try {
      await api.post(`/budget/revisions/${rev.id}/decision`, { decision, approveAll, note });
      toast.success(`Revision ${decision.toLowerCase()}`); setRevData(null); load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not update revision'); }
  };

  // ---- Booking (C6: Quantity at the ACTUAL rate; baseline rate is the ceiling) ----
  const [bookDialog, setBookDialog] = useState(null);
  const [bookForm, setBookForm] = useState({ qty: '', rate: '', reference: '', note: '' });
  const openBook = (line) => {
    setBookForm({ qty: '', rate: ((line.rate || 0) / 100).toFixed(2), reference: '', note: '' });
    setBookDialog(line);
  };
  const submitBooking = async () => {
    const qn = Number(bookForm.qty);
    if (!Number.isInteger(qn) || qn <= 0) {
      toast.error('Booked quantity must be a whole number greater than zero (no decimals).'); return;
    }
    const rn = Number(bookForm.rate);
    if (!(rn > 0)) { toast.error('Rate must be greater than zero.'); return; }
    if (rn > (bookDialog.rate || 0) / 100 + 1e-9) {
      toast.error('Rate cannot exceed the budgeted rate. Raise a rate revision to book above it.'); return;
    }
    try {
      const { data: res } = await api.post(`/budget/projects/${id}/bookings`, {
        lineId: bookDialog.lineId, qty: qn, rate: rn,
        reference: bookForm.reference, note: bookForm.note,
      });
      (res.warnings || []).forEach((w) => toast.warning(w));
      toast.success('Booking recorded');
      setBookDialog(null); refresh();
    } catch (e) { toast.error(e.response?.data?.detail || 'Booking failed'); }
  };

  // ---- C11 Budget Savings Capture (ring-fence under-budget surplus) ----
  const [savingsData, setSavingsData] = useState(null);
  const loadSavings = useCallback(async () => {
    try { const { data: s } = await api.get(`/budget/projects/${id}/savings`); setSavingsData(s); }
    catch { setSavingsData({ savings: [], totals: { saved: 0, released: 0 }, permissions: {} }); }
  }, [id]);
  useEffect(() => { if (tab === 'savings' && savingsData === null) loadSavings(); }, [tab, savingsData, loadSavings]);

  const [saveDialog, setSaveDialog] = useState(null); // the line being saved
  const [saveForm, setSaveForm] = useState({ amount: '', savingType: 'qty', reason: '' });
  const openSaving = (line) => { setSaveForm({ amount: '', savingType: 'qty', reason: '' }); setSaveDialog(line); };
  const submitSaving = async () => {
    const amt = Number(saveForm.amount);
    if (!(amt > 0)) { toast.error('Enter a saving amount greater than zero'); return; }
    try {
      await api.post(`/budget/projects/${id}/savings`, {
        lineId: saveDialog.lineId, amount: amt, savingType: saveForm.savingType, reason: saveForm.reason,
      });
      toast.success('Saving banked & ring-fenced'); setSaveDialog(null); setSavingsData(null); refresh();
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not mark saving'); }
  };
  const [releaseDialog, setReleaseDialog] = useState(null); // the saving being released
  const [releaseReason, setReleaseReason] = useState('');
  const openRelease = (sv) => { setReleaseReason(''); setReleaseDialog(sv); };
  const submitRelease = async () => {
    try {
      await api.post(`/budget/savings/${releaseDialog.id}/release`, { reason: releaseReason });
      toast.success('Saving released back to spendable budget'); setReleaseDialog(null); setSavingsData(null); refresh();
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not release saving'); }
  };

  const [teamOpen, setTeamOpen] = useState(false);
  const [candidates, setCandidates] = useState({ pms: [], engineers: [] });
  const [teamForm, setTeamForm] = useState({ projectManagerId: '', assignedEngineerIds: [] });
  const openTeam = async () => {
    setTeamForm({
      projectManagerId: data.project.projectManagerId || '',
      assignedEngineerIds: data.project.assignedEngineerIds || [],
    });
    try { const { data: c } = await api.get('/projects/team/candidates'); setCandidates(c); } catch (e) { console.warn('Failed to load team candidates', e); }
    setTeamOpen(true);
  };
  const submitTeam = async () => {
    try {
      await api.post(`/projects/${id}/team`, {
        projectManagerId: teamForm.projectManagerId || null,
        assignedEngineerIds: teamForm.assignedEngineerIds,
      });
      toast.success('Project team updated'); setTeamOpen(false); refresh();
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not update team'); }
  };
  const toggleEngineer = (eid) => setTeamForm((f) => ({
    ...f,
    assignedEngineerIds: f.assignedEngineerIds.includes(eid)
      ? f.assignedEngineerIds.filter((x) => x !== eid)
      : [...f.assignedEngineerIds, eid],
  }));

  // ---- Duration (C5, Admin-only) ----
  const [durOpen, setDurOpen] = useState(false);
  const [durForm, setDurForm] = useState({ startDate: '', endDate: '' });
  const openDuration = () => {
    setDurForm({ startDate: data.project.startDate || '', endDate: data.project.endDate || '' });
    setDurOpen(true);
  };
  const submitDuration = async () => {
    if (durForm.startDate && durForm.endDate && durForm.endDate < durForm.startDate) {
      toast.error('End date cannot be before the start date'); return;
    }
    try {
      await api.put(`/projects/${id}/duration`, { startDate: durForm.startDate || null, endDate: durForm.endDate || null });
      toast.success('Project duration updated'); setDurOpen(false); refresh();
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not update duration'); }
  };

  // Memoized tracker grouping (D3).
  const groupedCats = useMemo(() => {
    const lines = data?.budget?.lines || [];
    const cats = [];
    const catMap = {};
    lines.forEach((l) => {
      if (!catMap[l.category]) { catMap[l.category] = { name: l.category, lines: [] }; cats.push(catMap[l.category]); }
      catMap[l.category].lines.push(l);
    });
    const rollup = Object.fromEntries((data?.budget?.categoryRollup || []).map((c) => [c.category, c]));
    return { cats, rollupByCat: rollup };
  }, [data?.budget?.lines, data?.budget?.categoryRollup]);

  // C6 — Budget lines grouped into the Cost BOQ hierarchy (Sale Item → Category → Lines).
  const trackerSales = useMemo(() => {
    const order = (data?.computed?.saleItems || []).map((s) => s.itemNo);
    const desc = Object.fromEntries((data?.computed?.saleItems || []).map((s) => [s.itemNo, s.description]));
    return groupBySaleItem(data?.budget?.lines || [], order, desc);
  }, [data?.budget?.lines, data?.computed?.saleItems]);

  if (!data) return <div className="p-8"><div className="h-40 animate-pulse rounded-2xl bg-muted/40" /></div>;

  const { project, baseline, budget } = data;
  const computed = data.computed;
  const canBook = data.permissions?.canBook;
  const canMarkSaving = data.permissions?.canMarkSaving;
  const canReleaseSaving = data.permissions?.canReleaseSaving;
  const canAssign = data.permissions?.canAssign;
  const canEditDuration = data.permissions?.canEditDuration;
  const canOpenCostCentre = data.permissions?.canOpenCostCentre;
  const ccStatus = data.ccStatus || (data.opening?.opened ? 'OPEN' : 'AWARDED');
  const ccOpened = ccStatus === 'OPEN';
  const ccReady = !!data.opening?.ready;
  const ccApproved = !!data.opening?.approved;
  const ccApprovalStatus = data.opening?.approvalStatus || 'Draft';
  const ccOutstanding = data.opening?.outstanding || [];
  const totals = budget.totals;

  const pctSpent = totals.budget > 0 ? Math.round((totals.booked / totals.budget) * 1000) / 10 : 0;
  const health = pctSpent < 80 ? 'on_track' : pctSpent <= 100 ? 'at_risk' : 'over_budget';

  // C7 — Balance Duration (days from today to End; overdue / pre-start aware).
  const durInfo = (() => {
    const s = project.startDate ? new Date(`${project.startDate}T00:00:00`) : null;
    const e = project.endDate ? new Date(`${project.endDate}T00:00:00`) : null;
    if (!s && !e) return { label: 'Duration not set', tone: 'muted' };
    const today = new Date(); today.setHours(0, 0, 0, 0);
    if (s && today < s) { const d = Math.ceil((s - today) / 86400000); return { label: `Starts in ${d} day${d !== 1 ? 's' : ''}`, tone: 'muted' }; }
    if (e) {
      const d = Math.ceil((e - today) / 86400000);
      if (d < 0) return { label: `${Math.abs(d)} day${Math.abs(d) !== 1 ? 's' : ''} overdue`, tone: 'danger' };
      return { label: `${d} day${d !== 1 ? 's' : ''} left`, tone: d <= 14 ? 'warning' : 'success' };
    }
    return { label: '—', tone: 'muted' };
  })();
  // C8 — physical execution % (value-weighted); null until certified.
  const execPct = data.execution?.physicalExecutionPct ?? null;

  const { cats, rollupByCat } = groupedCats;

  // Sale-item section open/closed (default open).
  const siIsOpen = (it) => siOpen[it] !== false;
  const toggleSi = (it) => setSiOpen((s) => ({ ...s, [it]: s[it] === false ? true : false }));
  const execSiIsOpen = (it) => execSiOpen[it] !== false;
  const toggleExecSi = (it) => setExecSiOpen((s) => ({ ...s, [it]: s[it] === false ? true : false }));

  // FIX-A — master Collapse all / Expand all for the Budget Tracker Sale-Item sections.
  // Display-only: folds/unfolds every S-line section (and its categories) at once.
  const trackerSiKeys = trackerSales.map((si) => si.itemNo);
  const trackerAllOpen = trackerSiKeys.length > 0 && trackerSiKeys.every((it) => siOpen[it] !== false);
  const toggleTrackerAll = () => {
    if (trackerAllOpen) { const next = {}; trackerSiKeys.forEach((it) => { next[it] = false; }); setSiOpen(next); }
    else setSiOpen({});
  };

  // C7 — Baseline Cost BOQ collapse (keyed per item::category, collapsed by default).
  const boqKeys = (computed?.saleItems || []).flatMap((si) => si.costBoq.categories.map((c) => `${si.itemNo}::${c.name}`));
  const toggleBoqCat = (key) => setBoqExpanded((e) => ({ ...e, [key]: !e[key] }));
  const boqAllOpen = boqKeys.length > 0 && boqKeys.every((k) => boqExpanded[k]);
  const toggleBoqAll = () => { const next = {}; if (!boqAllOpen) boqKeys.forEach((k) => { next[k] = true; }); setBoqExpanded(next); };

  const Stat = ({ icon: Icon, label, value, tone, testid, valueClass }) => (
    <div className="erak-card flex items-center gap-3 p-4" data-testid={testid}>
      <span className={cn('grid h-10 w-10 shrink-0 place-items-center rounded-xl', tone || 'bg-muted/50')}><Icon size={18} /></span>
      <div className="min-w-0"><div className="text-[10px] uppercase tracking-wide text-muted-foreground">{label}</div>
        <div className={cn('money truncate text-lg font-bold', valueClass)}>{value}</div></div>
    </div>
  );

  return (
    <div className="space-y-6 p-4 md:p-6 lg:p-8" data-testid="budget-detail">
      <button onClick={() => navigate(basePath)} className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground"><ArrowLeft size={15} />{backLabel}</button>

      {/* C7 — sticky project totals bar (stays visible while scrolling the BOQ) */}
      <div data-testid="sticky-totals" className="sticky top-0 z-20 -mx-4 border-y bg-background/85 px-4 py-2.5 backdrop-blur md:-mx-6 md:px-6 lg:-mx-8 lg:px-8">
        <div className="flex flex-wrap items-center gap-x-6 gap-y-1.5 text-sm">
          <span className="font-num text-xs font-semibold text-[hsl(var(--copper))]">{project.code}</span>
          {tab !== 'overview' && (<>
            <span className="flex items-baseline gap-1.5"><span className="text-[10px] uppercase tracking-wide text-muted-foreground">Budget</span><span className="money font-semibold tabular-nums">{money(totals.budget)}</span></span>
            <span className="flex items-baseline gap-1.5"><span className="text-[10px] uppercase tracking-wide text-muted-foreground">Booked</span><span className="money font-semibold tabular-nums">{money(totals.booked)}</span></span>
            <span className="flex items-baseline gap-1.5"><span className="text-[10px] uppercase tracking-wide text-muted-foreground">Balance</span><span className={cn('money font-semibold tabular-nums', totals.overBudget && 'text-[hsl(var(--danger))]')}>{money(totals.balance)}</span></span>
          </>)}
          {isCosting && (
            <span className="flex items-baseline gap-1.5"><span className="text-[10px] uppercase tracking-wide text-muted-foreground">Savings</span><span data-testid="sticky-savings" className="money font-semibold tabular-nums text-[hsl(var(--copper))]">{money(totals.saved || 0)}</span></span>
          )}
          <span className="flex items-baseline gap-1.5"><span className="text-[10px] uppercase tracking-wide text-muted-foreground">Exec</span><span data-testid="sticky-exec" className="money font-semibold tabular-nums">{execPct == null ? '—' : `${execPct}%`}</span></span>
          <span className={cn('ml-auto flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[11px] font-medium',
            `bg-[hsl(var(--${durInfo.tone === 'muted' ? 'muted-foreground' : durInfo.tone})/0.12)]`,
            durInfo.tone === 'muted' ? 'text-muted-foreground' : `text-[hsl(var(--${durInfo.tone}))]`)} data-testid="sticky-balance-duration">
            <CalendarDays size={12} />{durInfo.label}
          </span>
        </div>
      </div>

      {/* Dashboard header */}
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <div className="flex items-center gap-2 font-num text-xs text-[hsl(var(--copper))]">{project.code}
            <Badge variant="outline" className="gap-1 text-[10px]"><Lock size={9} />Baseline frozen</Badge>
            {ccOpened ? (
              <Badge variant="outline" data-testid="cc-status-open" className="gap-1 border-[hsl(var(--success)/0.4)] bg-[hsl(var(--success)/0.12)] text-[10px] text-[hsl(var(--success))]"><CheckCircle2 size={9} />Cost centre OPEN</Badge>
            ) : (
              <Badge variant="outline" data-testid="cc-status-pending" className="gap-1 border-[hsl(var(--warning)/0.4)] bg-[hsl(var(--warning)/0.12)] text-[10px] text-[hsl(var(--warning))]"><Clock size={9} />AWARDED · opening pending</Badge>
            )}
            {tab !== 'overview' && <HealthPill health={health} testid="header-health" />}
          </div>
          <h1 className="font-display text-2xl font-bold md:text-3xl">{project.name}</h1>
          <p className="text-sm text-muted-foreground">{project.client} · Frozen {fmtDate(baseline.frozenAt)} by {baseline.frozenBy}</p>
          <div className="mt-2 flex flex-wrap items-center gap-2 text-xs" data-testid="project-team">
            <span className="flex items-center gap-1 text-muted-foreground"><Users size={13} />Team:</span>
            <Badge variant="secondary" className="gap-1">PM: {project.projectManagerName || 'Unassigned'}</Badge>
            {(project.assignedEngineerNames || []).length > 0
              ? project.assignedEngineerNames.map((n) => <Badge key={n} variant="outline">{n}</Badge>)
              : <span className="text-muted-foreground">No engineers assigned</span>}
            <span className="flex items-center gap-1 text-muted-foreground" data-testid="project-duration">
              <CalendarDays size={13} />
              {(project.startDate || project.endDate)
                ? <>{project.startDate ? fmtDate(project.startDate) : '—'} → {project.endDate ? fmtDate(project.endDate) : '—'}</>
                : <span>Duration not set</span>}
            </span>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {isCosting && canOpenCostCentre && !ccOpened && (
            <div className="flex flex-col items-end gap-1">
              <Button data-testid="open-cost-centre-button" onClick={openCostCentre} disabled={opening || !ccReady || !ccApproved}
                title={!ccReady ? `Complete the Opening form first (${ccOutstanding.length} outstanding)` : (!ccApproved ? 'The Cost Centre Opening must be approved first' : '')}
                className="bg-[hsl(var(--brand))] text-white hover:bg-[hsl(var(--brand)/0.9)]">
                <Rocket size={15} className="mr-2" />{opening ? 'Opening…' : 'Open Cost Centre / Push to Project'}
              </Button>
              {(!ccReady || !ccApproved) && (
                <span data-testid="open-cc-helper" className="text-[11px] text-[hsl(var(--warning))]">
                  {!ccReady
                    ? (ccOutstanding.length ? `${ccOutstanding.length} item(s) outstanding — see Cost Centre Opening` : 'Complete the Cost Centre Opening form first')
                    : ccApprovalStatus === 'Pending' ? 'Awaiting approval — see Cost Centre Opening'
                      : ccApprovalStatus === 'Rejected' ? 'Opening was rejected — revise & re-submit'
                        : 'Submit the Cost Centre Opening for approval first'}
                </span>
              )}
            </div>
          )}
          {isCosting && ccOpened && (
            <span data-testid="cc-opened-note" className="flex items-center gap-1.5 rounded-lg border border-[hsl(var(--success)/0.3)] bg-[hsl(var(--success)/0.08)] px-3 py-2 text-xs text-[hsl(var(--success))]">
              <CheckCircle2 size={14} />Opened by {data.opening?.openedBy || '—'}
            </span>
          )}
          {!isCosting && canEditDuration && (
            <Button variant="outline" data-testid="edit-duration-button" onClick={openDuration}>
              <CalendarDays size={15} className="mr-2" />Edit duration
            </Button>
          )}
          {!isCosting && canAssign && (
            <Button variant="outline" data-testid="assign-team-button" onClick={openTeam}>
              <UserPlus size={15} className="mr-2" />Assign team
            </Button>
          )}
        </div>
      </div>

      {tab !== 'overview' && (
      <div className={cn('grid gap-3 sm:grid-cols-2 lg:grid-cols-3', isCosting ? 'xl:grid-cols-5' : 'xl:grid-cols-4')}>
        <Stat testid="header-budget" icon={Wallet} label={`Cost Budget (${cur})`} value={money(totals.budget)} tone="bg-[hsl(var(--brand)/0.12)] text-[hsl(var(--brand))]" />
        <Stat testid="header-booked" icon={ClipboardList} label={`Booked (${cur})`} value={money(totals.booked)} tone="bg-[hsl(var(--teal)/0.12)] text-[hsl(var(--teal))]" />
        <div className="erak-card flex items-center gap-3 p-4" data-testid="header-balance">
          <span className={cn('grid h-10 w-10 shrink-0 place-items-center rounded-xl', totals.overBudget ? 'bg-[hsl(var(--danger)/0.12)] text-[hsl(var(--danger))]' : 'bg-[hsl(var(--success)/0.12)] text-[hsl(var(--success))]')}>
            {totals.overBudget ? <AlertTriangle size={18} /> : <TrendingDown size={18} />}</span>
          <div className="min-w-0"><div className="text-[10px] uppercase tracking-wide text-muted-foreground">Spendable Balance ({cur})</div>
            <div className={cn('money truncate text-lg font-bold', totals.overBudget && 'text-[hsl(var(--danger))]')} data-testid="budget-total-balance">{money(totals.balance)}</div></div>
        </div>
        {isCosting && (
          <div className="erak-card flex items-center gap-3 p-4" data-testid="header-savings">
            <span className="grid h-10 w-10 shrink-0 place-items-center rounded-xl bg-[hsl(var(--copper)/0.12)] text-[hsl(var(--copper))]"><PiggyBank size={18} /></span>
            <div className="min-w-0"><div className="text-[10px] uppercase tracking-wide text-muted-foreground">Savings banked ({cur})</div>
              <div className="money truncate text-lg font-bold text-[hsl(var(--copper))]" data-testid="budget-total-savings">{money(totals.saved || 0)}</div></div>
          </div>
        )}
        <div className="erak-card flex items-center gap-3 p-4" data-testid="header-pct-spent">
          <span className="grid h-10 w-10 shrink-0 place-items-center rounded-xl bg-[hsl(var(--copper)/0.12)] text-[hsl(var(--copper))]"><Scale size={18} /></span>
          <div className="min-w-0 flex-1">
            <div className="text-[10px] uppercase tracking-wide text-muted-foreground">% Spent</div>
            <div className="flex items-center gap-2"><span className="money text-lg font-bold tabular-nums">{pctSpent}%</span></div>
            <div className="mt-1 h-1.5 w-full overflow-hidden rounded-full bg-muted">
              <div className={cn('h-full rounded-full', `bg-[hsl(var(--${(HEALTH[health] || HEALTH.on_track).tone}))]`)} style={{ width: `${Math.min(pctSpent, 100)}%` }} />
            </div>
          </div>
        </div>
      </div>
      )}

      <Tabs value={tab} onValueChange={setTab}>
        <div ref={tabsWrapRef} className="-mx-4 overflow-x-auto px-4 no-scrollbar md:mx-0 md:overflow-visible md:px-0">
          <TabsList className="w-max md:w-auto">
            {!isCosting && <TabsTrigger value="overview" data-testid="tab-overview"><LayoutDashboard size={14} className="mr-1.5" />Overview</TabsTrigger>}
            {isCosting && <TabsTrigger value="opening" data-testid="tab-opening"><ClipboardList size={14} className="mr-1.5" />Cost Centre Opening</TabsTrigger>}
            {isCosting && <TabsTrigger value="tracker" data-testid="tab-tracker"><Wallet size={14} className="mr-1.5" />Budget Tracker</TabsTrigger>}
            {isCosting && <TabsTrigger value="invoice" data-testid="tab-invoice"><Receipt size={14} className="mr-1.5" />Invoices</TabsTrigger>}
            {isCosting && <TabsTrigger value="costcontrol" data-testid="tab-costcontrol"><Gauge size={14} className="mr-1.5" />Cost Control</TabsTrigger>}
            {!isCosting && <TabsTrigger value="execution" data-testid="tab-execution"><HardHat size={14} className="mr-1.5" />Site Execution</TabsTrigger>}
            {isCosting && <TabsTrigger value="revisions" data-testid="tab-revisions"><GitBranch size={14} className="mr-1.5" />Revisions</TabsTrigger>}
            {isCosting && <TabsTrigger value="savings" data-testid="tab-savings"><PiggyBank size={14} className="mr-1.5" />Savings</TabsTrigger>}
            <TabsTrigger value="history" data-testid="tab-history"><History size={14} className="mr-1.5" />History</TabsTrigger>
            <TabsTrigger value="boq" data-testid="tab-boq"><FileStack size={14} className="mr-1.5" />Baseline Cost BOQ</TabsTrigger>
          </TabsList>
        </div>
        {isMobile && (
          <div className="mt-2 flex items-center justify-center gap-1.5 text-[11px] text-muted-foreground" data-testid="tab-swipe-hint">
            <ChevronRight size={12} className="rotate-180" />Swipe to switch tabs<ChevronRight size={12} />
          </div>
        )}

        <div onTouchStart={isMobile ? onTabTouchStart : undefined} onTouchEnd={isMobile ? onTabTouchEnd : undefined}>

        {/* Overview (Project module) — execution-only at-a-glance summary. No commercial data. */}
        <TabsContent value="overview" className="mt-4" data-testid="overview-tab">
          <ProjectOverview projectId={id} />
        </TabsContent>

        {/* Cost Centre Opening (P2) — setup form + departmental sign-off */}
        <TabsContent value="opening" className="mt-4" data-testid="opening-tab">
          <CostCentreOpening key={ccOpened ? 'opened' : 'draft'} projectId={id} currency={cur} onOpened={refresh} />
        </TabsContent>

        {/* Cost Control dashboard (P4) — Budget vs Committed vs Actual, margins, actuals */}
        <TabsContent value="costcontrol" className="mt-4" data-testid="costcontrol-tab">
          <CostControl projectId={id} currency={cur} />
        </TabsContent>

        {/* Invoice Plan (P3) — milestone/POC, %-driven with retention & advance recovery */}
        <TabsContent value="invoice" className="mt-4" data-testid="invoice-tab">
          <InvoicePlan projectId={id} currency={cur} />
        </TabsContent>

        {/* Budget Tracker — Qty × Rate (C6). Costing books one cost category at a time. */}
        <TabsContent value="tracker" className="mt-4 space-y-4">
          {/* Per-category reporting summary (Material / Labour / Vehicle / Subcon ...) */}
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4" data-testid="tracker-category-summary">
            {cats.map((cat) => {
              const cr = rollupByCat[cat.name] || {};
              const pct = cr.budget > 0 ? Math.round((cr.booked / cr.budget) * 1000) / 10 : 0;
              const tone = pct < 80 ? 'success' : pct <= 100 ? 'warning' : 'danger';
              return (
                <div key={cat.name} data-testid={`cat-card-${cat.name}`} className="erak-card p-4">
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-semibold uppercase tracking-wide">{cat.name}</span>
                    <span className="rounded-full bg-muted px-1.5 text-[10px] font-medium text-muted-foreground">{cat.lines.length}</span>
                  </div>
                  <div className="mt-2 space-y-1 text-xs">
                    <div className="flex items-center justify-between gap-2"><span className="text-muted-foreground">Budget</span><span className="money">{money(cr.budget)}</span></div>
                    <div className="flex items-center justify-between gap-2"><span className="text-muted-foreground">Booked</span><span className="money text-[hsl(var(--teal))]">{money(cr.booked)}</span></div>
                    <div className="flex items-center justify-between gap-2"><span className="text-muted-foreground">Balance</span><span className={cn('money', cr.overBudget && 'text-[hsl(var(--danger))]')}>{money(cr.balance)}</span></div>
                  </div>
                  <div className="mt-2 flex items-center justify-between text-[10px] text-muted-foreground"><span>% Spent</span><span className="font-num tabular-nums">{pct}%</span></div>
                  <div className="mt-1 h-1.5 w-full overflow-hidden rounded-full bg-muted">
                    <div className={cn('h-full rounded-full', `bg-[hsl(var(--${tone}))]`)} style={{ width: `${Math.min(pct, 100)}%` }} />
                  </div>
                </div>
              );
            })}
          </div>

          {/* Cost BOQ hierarchy: Sale Item → cost category → lines */}
          {trackerSales.length > 0 && (
            <div className="flex items-center justify-between gap-2">
              <h3 className="font-display text-base font-semibold">Cost BOQ — by sale item</h3>
              <Button variant="ghost" size="sm" className="h-8 text-xs" data-testid="tracker-collapse-all" onClick={toggleTrackerAll}>
                {trackerAllOpen ? <ChevronsDownUp size={14} className="mr-1.5" /> : <ChevronsUpDown size={14} className="mr-1.5" />}
                {trackerAllOpen ? 'Collapse all' : 'Expand all'}
              </Button>
            </div>
          )}
          {trackerSales.map((si) => {
            const open = siIsOpen(si.itemNo);
            const siBudget = sumBy(si.lines, (l) => l.workingBudget);
            const siBooked = sumBy(si.lines, (l) => l.booked);
            const siBalance = sumBy(si.lines, (l) => l.balance);
            const siSaved = sumBy(si.lines, (l) => l.saved);
            return (
              <div key={si.itemNo} className="erak-card overflow-hidden" data-testid={`tracker-saleitem-${si.itemNo}`}>
                <button type="button" onClick={() => toggleSi(si.itemNo)} data-testid={`tracker-saleitem-toggle-${si.itemNo}`}
                  className="flex w-full items-center gap-3 border-b bg-muted/30 px-4 py-2.5 text-left hover:bg-muted/40">
                  <ChevronRight size={15} className={cn('shrink-0 transition-transform', open && 'rotate-90')} />
                  <span className="font-num text-xs text-[hsl(var(--copper))]">{si.itemNo}</span>
                  <span className="truncate text-sm font-semibold">{si.description || 'Sale item'}</span>
                  <span className="ml-auto flex flex-wrap items-center gap-x-4 gap-y-0.5 text-xs">
                    <span className="text-muted-foreground">Budget <span className="money font-semibold text-foreground">{money(siBudget)}</span></span>
                    <span className="text-muted-foreground">Booked <span className="money font-semibold text-[hsl(var(--teal))]">{money(siBooked)}</span></span>
                    {siSaved > 0 && <span className="text-muted-foreground">Saved <span className="money font-semibold text-[hsl(var(--copper))]">{money(siSaved)}</span></span>}
                    <span className="text-muted-foreground">Balance <span className={cn('money font-semibold', siBalance < 0 ? 'text-[hsl(var(--danger))]' : 'text-foreground')}>{money(siBalance)}</span></span>
                  </span>
                </button>
                {open && (
                  <div className="overflow-x-auto">
                    <table className="w-full min-w-[960px] text-sm" data-testid="budget-tracker-table">
                      <thead>
                        <tr className="border-b bg-muted/40 text-left text-[11px] uppercase tracking-wide text-muted-foreground">
                          <th className="px-3 py-2.5 font-semibold">Item</th>
                          <th className="px-3 py-2.5 text-right font-semibold">Budget Qty</th>
                          <th className="px-3 py-2.5 text-right font-semibold">Rate</th>
                          <th className="px-3 py-2.5 text-right font-semibold">Budget</th>
                          <th className="px-3 py-2.5 text-right font-semibold">Bkd Qty</th>
                          <th className="px-3 py-2.5 text-right font-semibold">Booked</th>
                          <th className="px-3 py-2.5 text-right font-semibold">Saved</th>
                          <th className="px-3 py-2.5 text-right font-semibold">Bal Qty</th>
                          <th className="px-3 py-2.5 text-right font-semibold">Balance</th>
                          {(canBook || canMarkSaving) && <th className="px-3 py-2.5" />}
                        </tr>
                      </thead>
                      <tbody>
                        {si.catOrder.map((catName) => {
                          const clines = si.cats[catName].lines;
                          const cBudget = sumBy(clines, (l) => l.workingBudget);
                          const cBooked = sumBy(clines, (l) => l.booked);
                          const cBalance = sumBy(clines, (l) => l.balance);
                          const cSaved = sumBy(clines, (l) => l.saved);
                          return (
                            <React.Fragment key={catName}>
                              <tr className="border-b bg-muted/20 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                                <td className="px-3 py-2 pl-8">{catName}<span className="ml-1 rounded-full bg-muted px-1.5 text-[10px] font-medium normal-case">{clines.length}</span></td>
                                <td /><td />
                                <td className="px-3 py-2 text-right money normal-case">{money(cBudget)}</td>
                                <td /><td className="px-3 py-2 text-right money normal-case">{money(cBooked)}</td>
                                <td className="px-3 py-2 text-right money normal-case text-[hsl(var(--copper))]">{cSaved > 0 ? money(cSaved) : ''}</td>
                                <td />
                                <td className={cn('px-3 py-2 text-right money normal-case', cBalance < 0 && 'text-[hsl(var(--danger))]')}>{money(cBalance)}</td>
                                {(canBook || canMarkSaving) && <td />}
                              </tr>
                              {clines.map((l) => (
                                <tr key={l.lineId} data-testid={`budget-line-${l.lineId}`} className="border-b last:border-0 hover:bg-muted/30">
                                  <td className="px-3 py-2.5 pl-12">
                                    <div className="flex flex-wrap items-center gap-2">
                                      {l.code && <span className="font-num text-xs text-[hsl(var(--copper))]">{l.code}</span>}
                                      <span className="text-sm">{l.description}</span>
                                      {l.revised && <Badge variant="secondary" className="gap-1 text-[10px] text-[hsl(var(--warning))]"><GitBranch size={9} />Rev {l.revisedFrom}</Badge>}
                                    </div>
                                  </td>
                                  <td className="whitespace-nowrap px-3 py-2.5 text-right font-num tabular-nums">{fmtInt(l.budgetQty)}{l.unit ? <span className="text-[10px] text-muted-foreground"> {l.unit}</span> : ''}</td>
                                  <td className="px-3 py-2.5 text-right money text-muted-foreground">{money(l.rate)}</td>
                                  <td className="px-3 py-2.5 text-right money">{money(l.workingBudget)}</td>
                                  <td className="px-3 py-2.5 text-right font-num tabular-nums">{fmtInt(l.bookedQty)}</td>
                                  <td className="px-3 py-2.5 text-right money">{money(l.booked)}</td>
                                  <td className="px-3 py-2.5 text-right">
                                    {l.saved > 0
                                      ? <span className="inline-flex items-center gap-1 rounded-full bg-[hsl(var(--copper)/0.12)] px-1.5 py-0.5 text-[11px] font-medium text-[hsl(var(--copper))]" data-testid={`line-saved-${l.lineId}`}><Lock size={9} />{money(l.saved)}</span>
                                      : <span className="text-muted-foreground">—</span>}
                                  </td>
                                  <td className={cn('px-3 py-2.5 text-right font-num tabular-nums', l.balanceQty < 0 && 'text-[hsl(var(--danger))]')}>{fmtInt(l.balanceQty)}</td>
                                  <td className={cn('px-3 py-2.5 text-right money font-medium', l.overBudget && 'text-[hsl(var(--danger))]')}>{money(l.balance)}</td>
                                  {(canBook || canMarkSaving) && (
                                    <td className="px-3 py-2.5 text-right">
                                      <div className="flex items-center justify-end gap-1.5">
                                        {canMarkSaving && (
                                          <Button size="sm" variant="ghost" className="h-7 px-2 text-[hsl(var(--copper))] hover:text-[hsl(var(--copper))]"
                                            data-testid={`mark-saving-${l.lineId}`} disabled={l.balance <= 0}
                                            title="Mark surplus as saving" onClick={() => openSaving(l)}>
                                            <PiggyBank size={14} className="mr-1" />Save
                                          </Button>
                                        )}
                                        {canBook && (
                                          <Button size="sm" variant="outline" className="h-7" data-testid={`book-now-${l.lineId}`}
                                            disabled={l.balanceQty <= 0} onClick={() => openBook(l)}>
                                            <Plus size={13} className="mr-1" />Book
                                          </Button>
                                        )}
                                      </div>
                                    </td>
                                  )}
                                </tr>
                              ))}
                            </React.Fragment>
                          );
                        })}
                        <tr className="border-t-2 bg-muted/30 font-bold">
                          <td className="px-3 py-3" colSpan={3}>{si.itemNo} TOTAL</td>
                          <td className="px-3 py-3 text-right money">{money(siBudget)}</td>
                          <td />
                          <td className="px-3 py-3 text-right money">{money(siBooked)}</td>
                          <td className="px-3 py-3 text-right money text-[hsl(var(--copper))]">{siSaved > 0 ? money(siSaved) : ''}</td>
                          <td />
                          <td className={cn('px-3 py-3 text-right money', siBalance < 0 && 'text-[hsl(var(--danger))]')}>{money(siBalance)}</td>
                          {(canBook || canMarkSaving) && <td />}
                        </tr>
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            );
          })}
          <p className="mt-1 text-[11px] text-muted-foreground">Bookings follow the Cost BOQ structure — Sale Item → cost category → line — at the frozen tender rate. To change a quantity or rate, raise a budget revision. Totals reconcile exactly to the frozen baseline.</p>
        </TabsContent>

        {/* C8 — Site Execution (certified executed qty vs budget) */}
        <TabsContent value="execution" className="mt-4 space-y-4" data-testid="execution-tab">
          {!ccOpened && (
            <div data-testid="execution-awaiting-banner" className="flex items-start gap-3 rounded-2xl border border-[hsl(var(--warning)/0.4)] bg-[hsl(var(--warning)/0.1)] p-4">
              <Clock size={20} className="mt-0.5 shrink-0 text-[hsl(var(--warning))]" />
              <div>
                <div className="font-semibold text-[hsl(var(--warning))]">Awaiting cost centre opening</div>
                <p className="text-sm text-muted-foreground">Site Execution is locked until Costing opens the cost centre and pushes this project to the Project team. You can review the plan below, but certification is disabled for now.</p>
              </div>
            </div>
          )}
          {execData === null ? (
            <div className="h-64 animate-pulse rounded-2xl bg-muted/40" />
          ) : (() => {
            const m = execData.metrics || { lines: [], categories: [] };
            const flag = EXEC_FLAG[m.flag] || EXEC_FLAG.productive;
            const canCertify = execData.permissions?.canCertify && ccOpened;
            const trend = (execData.trend || []).map((t) => ({
              date: fmtDate(t.at), Execution: t.physicalExecutionPct,
              Consumed: t.consumedPct, Time: t.timeElapsedPct,
            }));
            const exSiOrder = (computed?.saleItems || []).map((s) => s.itemNo);
            const exSiDesc = Object.fromEntries((computed?.saleItems || []).map((s) => [s.itemNo, s.description]));
            const execSales = groupBySaleItem(m.lines || [], exSiOrder, exSiDesc);
            // FIX-A — master Collapse all / Expand all for Site Execution Sale-Item sections.
            const execSiKeys = execSales.map((si) => si.itemNo);
            const execAllOpen = execSiKeys.length > 0 && execSiKeys.every((it) => execSiOpen[it] !== false);
            const toggleExecAll = () => {
              if (execAllOpen) { const next = {}; execSiKeys.forEach((it) => { next[it] = false; }); setExecSiOpen(next); }
              else setExecSiOpen({});
            };
            const tooltipStyle = { background: 'hsl(var(--popover))', border: '1px solid hsl(var(--border))', borderRadius: 10, fontSize: 12 };
            const Gauge = ({ label, value, tone, testid }) => (
              <div className="erak-card p-4" data-testid={testid}>
                <div className="text-[10px] uppercase tracking-wide text-muted-foreground">{label}</div>
                <div className={cn('money mt-0.5 text-2xl font-bold tabular-nums', tone && `text-[hsl(var(--${tone}))]`)}>{value == null ? '—' : `${value}%`}</div>
                <div className="mt-2 h-1.5 w-full overflow-hidden rounded-full bg-muted">
                  <div className={cn('h-full rounded-full', tone ? `bg-[hsl(var(--${tone}))]` : 'bg-[hsl(var(--brand))]')} style={{ width: `${Math.min(value || 0, 100)}%` }} />
                </div>
              </div>
            );
            return (
              <>
                {/* Productivity panel */}
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <div>
                    <h3 className="font-display text-base font-semibold">Productivity</h3>
                    <p className="text-xs text-muted-foreground">Physical execution (value-weighted) vs time elapsed vs resources consumed.{execData.lastCertifiedAt ? ` Last certified ${fmtDate(execData.lastCertifiedAt)} by ${execData.lastCertifiedBy}.` : ' Not yet certified.'}</p>
                  </div>
                  <span data-testid="execution-flag" className={cn('inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-semibold',
                    `bg-[hsl(var(--${flag.tone})/0.12)] text-[hsl(var(--${flag.tone}))]`)}>
                    <HardHat size={13} />{flag.label}
                  </span>
                </div>
                <div className="grid gap-3 sm:grid-cols-3">
                  <Gauge testid="exec-physical" label="Physical Execution" value={m.physicalExecutionPct} tone={flag.tone} />
                  <Gauge testid="exec-time" label="Time Elapsed" value={m.timeElapsedPct} />
                  <Gauge testid="exec-consumed" label="Resources Consumed (budget)" value={m.consumedPct} />
                </div>

                {/* Per-category execution summary (Material / Labour / Vehicle ...) */}
                {(m.categories || []).length > 0 && (
                  <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4" data-testid="exec-category-summary">
                    {m.categories.map((c) => {
                      const pct = c.executedPct ?? 0;
                      const cTone = pct >= 90 ? 'success' : pct >= 50 ? 'warning' : 'danger';
                      return (
                        <div key={c.category} className="erak-card p-4" data-testid={`exec-cat-card-${c.category}`}>
                          <div className="flex items-center justify-between">
                            <span className="text-xs font-semibold uppercase tracking-wide">{c.category}</span>
                            <span className="font-num text-xs tabular-nums text-muted-foreground">{pct}%</span>
                          </div>
                          <div className="mt-2 space-y-1 text-xs">
                            <div className="flex items-center justify-between gap-2"><span className="text-muted-foreground">Budget</span><span className="money">{money(c.budget)}</span></div>
                            <div className="flex items-center justify-between gap-2"><span className="text-muted-foreground">Earned</span><span className="money text-[hsl(var(--success))]">{money(c.earned)}</span></div>
                          </div>
                          <div className="mt-2 h-1.5 w-full overflow-hidden rounded-full bg-muted">
                            <div className={cn('h-full rounded-full', `bg-[hsl(var(--${cTone}))]`)} style={{ width: `${Math.min(pct, 100)}%` }} />
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}

                {/* Trend chart */}
                {trend.length > 0 && (
                  <div className="erak-card p-4" data-testid="execution-trend">
                    <div className="mb-2 text-sm font-semibold">Productivity trend</div>
                    <ResponsiveContainer width="100%" height={240}>
                      <LineChart data={trend} margin={{ top: 5, right: 10, bottom: 0, left: -20 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                        <XAxis dataKey="date" tick={{ fill: 'hsl(var(--muted-foreground))', fontSize: 11 }} axisLine={false} tickLine={false} />
                        <YAxis tick={{ fill: 'hsl(var(--muted-foreground))', fontSize: 11 }} axisLine={false} tickLine={false} unit="%" />
                        <RTooltip contentStyle={tooltipStyle} formatter={(v) => `${v}%`} />
                        <Legend wrapperStyle={{ fontSize: 11 }} />
                        <Line type="monotone" dataKey="Execution" stroke="hsl(var(--success))" strokeWidth={2} dot={{ r: 3 }} />
                        <Line type="monotone" dataKey="Consumed" stroke="hsl(var(--danger))" strokeWidth={2} dot={{ r: 3 }} />
                        <Line type="monotone" dataKey="Time" stroke="hsl(var(--muted-foreground))" strokeWidth={1.5} strokeDasharray="4 4" dot={false} />
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                )}

                {/* Per-line certification — Cost BOQ hierarchy: Sale Item → category → lines */}
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <h3 className="font-display text-base font-semibold">Executed quantity by line</h3>
                  <div className="flex items-center gap-2">
                    {execSales.length > 0 && (
                      <Button variant="ghost" size="sm" className="h-8 text-xs" data-testid="exec-collapse-all" onClick={toggleExecAll}>
                        {execAllOpen ? <ChevronsDownUp size={14} className="mr-1.5" /> : <ChevronsUpDown size={14} className="mr-1.5" />}
                        {execAllOpen ? 'Collapse all' : 'Expand all'}
                      </Button>
                    )}
                    {canCertify && (
                      <Button size="sm" className="h-8" data-testid="certify-execution-button" onClick={submitExecution} disabled={Object.keys(execEdits).length === 0 && Object.keys(deliveredEdits).length === 0}>
                        <HardHat size={14} className="mr-1.5" />Certify execution
                      </Button>
                    )}
                  </div>
                </div>
                {execSales.map((si) => {
                  const open = execSiIsOpen(si.itemNo);
                  const siBudget = sumBy(si.lines, (l) => l.budget);
                  const siEarned = sumBy(si.lines, (l) => l.earned);
                  const siPct = siBudget > 0 ? Math.round((siEarned / siBudget) * 1000) / 10 : 0;
                  return (
                    <div key={si.itemNo} className="erak-card overflow-hidden" data-testid={`exec-saleitem-${si.itemNo}`}>
                      <button type="button" onClick={() => toggleExecSi(si.itemNo)} data-testid={`exec-saleitem-toggle-${si.itemNo}`}
                        className="flex w-full items-center gap-3 border-b bg-muted/30 px-4 py-2.5 text-left hover:bg-muted/40">
                        <ChevronRight size={15} className={cn('shrink-0 transition-transform', open && 'rotate-90')} />
                        <span className="font-num text-xs text-[hsl(var(--copper))]">{si.itemNo}</span>
                        <span className="truncate text-sm font-semibold">{si.description || 'Sale item'}</span>
                        <span className="ml-auto text-xs text-muted-foreground">Exec <span className="font-num font-semibold text-foreground">{siPct}%</span></span>
                      </button>
                      {open && (
                        <div className="overflow-x-auto">
                          <table className="w-full min-w-[820px] text-sm" data-testid="execution-table">
                            <thead><tr className="border-b bg-muted/40 text-left text-[11px] uppercase tracking-wide text-muted-foreground">
                              <th className="px-3 py-2 font-semibold">Line</th>
                              <th className="px-3 py-2 text-right font-semibold">Budget Qty</th>
                              <th className="px-3 py-2 text-right font-semibold">Delivered Qty</th>
                              <th className="px-3 py-2 text-right font-semibold">Executed Qty</th>
                              <th className="px-3 py-2 text-right font-semibold">Balance Qty</th>
                              <th className="px-3 py-2 text-right font-semibold">Exec %</th>
                            </tr></thead>
                            <tbody>
                              {si.catOrder.map((catName) => {
                                const clines = si.cats[catName].lines;
                                const cBudget = sumBy(clines, (l) => l.budget);
                                const cEarned = sumBy(clines, (l) => l.earned);
                                const cPct = cBudget > 0 ? Math.round((cEarned / cBudget) * 1000) / 10 : 0;
                                return (
                                  <React.Fragment key={catName}>
                                    <tr className="border-b bg-muted/20 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                                      <td className="px-3 py-1.5 pl-8">{catName}<span className="ml-1 rounded-full bg-muted px-1.5 text-[10px] font-medium normal-case">{clines.length}</span></td>
                                      <td /><td /><td /><td />
                                      <td className="px-3 py-1.5 text-right normal-case">{cPct}%</td>
                                    </tr>
                                    {clines.map((l) => {
                                      const edited = execEdits[l.lineId];
                                      const shownExec = edited !== undefined ? edited : l.executedQty;
                                      const exNum = Number(shownExec) || 0;
                                      const balQty = (l.budgetQty || 0) - exNum;
                                      const pct = l.budgetQty ? Math.round((exNum / l.budgetQty) * 1000) / 10 : 0;
                                      // Delivered Qty — MATERIAL lines only. Independent field; never
                                      // affects Balance (Budget − Executed) or Exec % above.
                                      const isMaterial = l.category === 'MATERIAL';
                                      const delEdited = deliveredEdits[l.lineId];
                                      const shownDel = delEdited !== undefined ? delEdited : (l.deliveredQty ?? 0);
                                      const delNum = Number(shownDel) || 0;
                                      const execOverDelivered = isMaterial && exNum > delNum;      // red: installed > arrived
                                      const overProcured = isMaterial && delNum > (l.budgetQty || 0); // amber: over-procurement
                                      return (
                                        <tr key={l.lineId} data-testid={`exec-line-${l.lineId}`} className="border-b last:border-0 hover:bg-muted/30">
                                          <td className="px-3 py-2 pl-12">
                                            {l.code && <span className="font-num text-xs text-[hsl(var(--copper))]">{l.code} </span>}{l.description}
                                          </td>
                                          <td className="whitespace-nowrap px-3 py-2 text-right font-num tabular-nums">{fmtInt(l.budgetQty)}{l.unit ? <span className="text-[10px] text-muted-foreground"> {l.unit}</span> : ''}</td>
                                          <td className="px-3 py-2 text-right" data-testid={`delivered-cell-${l.lineId}`}>
                                            {!isMaterial ? (
                                              <span className="font-num tabular-nums text-muted-foreground" title="Delivered quantity applies to material lines only">—</span>
                                            ) : canCertify ? (
                                              <div className="ml-auto flex w-fit flex-col items-end gap-0.5">
                                                <NumberStepper value={delNum} step={1} decimals={0} min={0}
                                                  data-testid={`delivered-input-${l.lineId}`} className="ml-auto" inputClassName="w-14"
                                                  onChange={(v) => setDeliveredEdits((s) => ({ ...s, [l.lineId]: String(v) }))} />
                                                {(execOverDelivered || overProcured) && (
                                                  <span data-testid={`delivered-warning-${l.lineId}`}
                                                    className={cn('inline-flex items-center gap-1 text-[10px]',
                                                      execOverDelivered ? 'text-[hsl(var(--danger))]' : 'text-[hsl(var(--warning))]')}>
                                                    <AlertTriangle size={10} />{execOverDelivered ? 'Executed exceeds delivered' : 'Over-procured'}
                                                  </span>
                                                )}
                                              </div>
                                            ) : (
                                              <span className={cn('inline-flex items-center gap-1 font-num tabular-nums',
                                                execOverDelivered ? 'text-[hsl(var(--danger))]' : overProcured ? 'text-[hsl(var(--warning))]' : '')}
                                                title={execOverDelivered ? 'Executed exceeds delivered' : overProcured ? 'Delivered exceeds budget (over-procurement)' : undefined}>
                                                {(execOverDelivered || overProcured) && <AlertTriangle size={11} />}
                                                {fmtInt(delNum)}
                                              </span>
                                            )}
                                          </td>
                                          <td className="px-3 py-2 text-right">
                                            {canCertify ? (
                                              <NumberStepper value={Number(shownExec) || 0} step={1} decimals={0} min={0} max={l.budgetQty || 0}
                                                data-testid={`exec-input-${l.lineId}`} className="ml-auto" inputClassName="w-14"
                                                onChange={(v) => setExecEdits((s) => ({ ...s, [l.lineId]: String(v) }))} />
                                            ) : (
                                              <span className="font-num tabular-nums">{fmtInt(l.executedQty)}</span>
                                            )}
                                          </td>
                                          <td className={cn('px-3 py-2 text-right font-num tabular-nums', balQty < 0 && 'text-[hsl(var(--danger))]')}>{fmtInt(balQty)}</td>
                                          <td className="px-3 py-2 text-right">
                                            <span className={cn('font-num tabular-nums', pct > 100 && 'text-[hsl(var(--danger))]')}>{pct}%</span>
                                          </td>
                                        </tr>
                                      );
                                    })}
                                  </React.Fragment>
                                );
                              })}
                            </tbody>
                          </table>
                        </div>
                      )}
                    </div>
                  );
                })}
              </>
            );
          })()}
        </TabsContent>

        {/* C9 — Budget Revisions (qty/rate change → sequential approval → effective) */}
        <TabsContent value="revisions" className="mt-4 space-y-4" data-testid="revisions-tab">
          <div className="flex flex-wrap items-end justify-between gap-2">
            <div>
              <h3 className="font-display text-base font-semibold">Budget Revisions</h3>
              <p className="max-w-xl text-xs text-muted-foreground">Propose a quantity or rate change on any baseline line. Each revision is routed sequentially through the approval chain and only changes the effective budget once fully approved.</p>
            </div>
            {data.permissions?.canRaiseRevision && (
              <Button data-testid="raise-revision-button" onClick={openRevision}>
                <GitBranch size={15} className="mr-2" />Raise revision
              </Button>
            )}
          </div>

          {revData === null ? (
            <div className="space-y-3">{[0, 1].map((i) => <div key={i} className="h-32 animate-pulse rounded-2xl bg-muted/40" />)}</div>
          ) : (revData.revisions || []).length === 0 ? (
            <div className="erak-card grid place-items-center gap-2 p-10 text-center" data-testid="revisions-empty">
              <GitBranch size={28} className="text-muted-foreground/60" />
              <div className="text-sm font-medium">No revisions yet</div>
              <p className="max-w-sm text-xs text-muted-foreground">When a quantity or rate needs to change, raise a revision and it will be routed for sequential approval here.</p>
            </div>
          ) : (
            <div className="space-y-3">
              {revData.revisions.map((rev) => {
                const steps = buildStepper(rev, revData.stages);
                const tone = REV_TONE[rev.status] || 'muted-foreground';
                return (
                  <div key={rev.id} className="erak-card p-4" data-testid={`revision-card-${rev.revNo}`}>
                    {/* Header */}
                    <div className="flex flex-wrap items-start justify-between gap-2">
                      <div className="min-w-0">
                        <div className="flex flex-wrap items-center gap-2">
                          <span className="font-num text-sm font-semibold text-[hsl(var(--copper))]">REV-{String(rev.revNo).padStart(2, '0')}</span>
                          <Badge data-testid={`revision-status-${rev.revNo}`} className={cn('gap-1 text-[10px]',
                            `bg-[hsl(var(--${tone})/0.12)] text-[hsl(var(--${tone}))]`)} variant="outline">
                            <CircleDot size={10} />{rev.status}
                          </Badge>
                          {rev.status === 'Approved' && rev.totalBudgetDelta !== 0 && (
                            <Badge variant="outline" className={cn('text-[10px]', rev.totalBudgetDelta > 0 ? 'text-[hsl(var(--danger))]' : 'text-[hsl(var(--success))]')}>
                              {rev.totalBudgetDelta > 0 ? '+' : ''}{money(rev.totalBudgetDelta)} budget
                            </Badge>
                          )}
                        </div>
                        <p className="mt-1 text-xs text-muted-foreground">
                          Raised by {rev.raisedBy}{rev.raisedByGroup ? ` (${rev.raisedByGroup})` : ''} · {fmtDate(rev.createdAt)}
                        </p>
                        <p className="mt-1 text-sm">{rev.reason}</p>
                      </div>
                    </div>

                    {/* Horizontal approval stepper */}
                    <div className="mt-3 flex items-start overflow-x-auto pb-1" data-testid={`rev-stepper-${rev.revNo}`}>
                      {steps.map((s, i) => {
                        const meta = STEP_META[s.status] || STEP_META.upcoming;
                        const SIcon = meta.Icon;
                        const connectorTone = s.status === 'rejected' ? 'danger'
                          : (s.status === 'approved' || s.status === 'raised') ? 'success' : null;
                        return (
                          <React.Fragment key={s.stage.key}>
                            {i > 0 && (
                              <div className={cn('mt-3.5 h-0.5 w-6 shrink-0 sm:w-10',
                                connectorTone ? `bg-[hsl(var(--${connectorTone}))]` : 'bg-border')} />
                            )}
                            <div className="flex w-16 shrink-0 flex-col items-center gap-1 sm:w-20" data-testid={`rev-step-${rev.revNo}-${s.stage.key}`}>
                              <span className={cn('grid h-7 w-7 place-items-center rounded-full ring-4 ring-background',
                                `bg-[hsl(var(--${meta.tone})/0.15)] text-[hsl(var(--${meta.tone}))]`,
                                s.status === 'current' && 'animate-pulse ring-[hsl(var(--warning)/0.2)]')}>
                                <SIcon size={13} />
                              </span>
                              <span className="text-center text-[10px] font-medium leading-tight">{s.stage.label}</span>
                              {s.by && <span className="max-w-full truncate text-center text-[9px] text-muted-foreground" title={s.by}>{s.override ? '⚡ ' : ''}{s.by}</span>}
                              {s.status === 'current' && <span className="text-[9px] text-[hsl(var(--warning))]">Pending</span>}
                            </div>
                          </React.Fragment>
                        );
                      })}
                    </div>

                    {/* Line changes */}
                    <div className="mt-3 overflow-x-auto rounded-xl border">
                      <table className="w-full min-w-[560px] text-sm">
                        <thead><tr className="border-b bg-muted/40 text-left text-[10px] uppercase tracking-wide text-muted-foreground">
                          <th className="px-3 py-2 font-semibold">Line</th>
                          <th className="px-3 py-2 text-right font-semibold">Current Qty</th>
                          <th className="px-3 py-2 text-right font-semibold">New Qty</th>
                          <th className="px-3 py-2 text-right font-semibold">Current Rate</th>
                          <th className="px-3 py-2 text-right font-semibold">New Rate</th>
                          <th className="px-3 py-2 text-right font-semibold">Δ Amount</th>
                        </tr></thead>
                        <tbody>
                          {rev.lines.map((ln) => {
                            const delta = Math.round(ln.newQty * ln.newRate) - Math.round(ln.currentQty * ln.currentRate);
                            const qtyChanged = ln.newQty !== ln.currentQty;
                            const rateChanged = ln.newRate !== ln.currentRate;
                            return (
                              <tr key={ln.lineId} className="border-b last:border-0" data-testid={`rev-line-${rev.revNo}-${ln.lineId}`}>
                                <td className="px-3 py-2">
                                  {ln.code && <span className="font-num text-xs text-[hsl(var(--copper))]">{ln.code} </span>}
                                  {ln.description}
                                  {ln.itemNo && <Badge variant="outline" className="ml-1 text-[10px]">{ln.itemNo}</Badge>}
                                </td>
                                <td className="px-3 py-2 text-right font-num tabular-nums text-muted-foreground">{fmtInt(ln.currentQty)}</td>
                                <td className={cn('px-3 py-2 text-right font-num tabular-nums', qtyChanged && 'font-semibold text-[hsl(var(--brand))]')}>{fmtInt(ln.newQty)}</td>
                                <td className="px-3 py-2 text-right money text-muted-foreground">{money(ln.currentRate)}</td>
                                <td className={cn('px-3 py-2 text-right money', rateChanged && 'font-semibold text-[hsl(var(--brand))]')}>{money(ln.newRate)}</td>
                                <td className={cn('px-3 py-2 text-right money font-medium', delta > 0 ? 'text-[hsl(var(--danger))]' : delta < 0 ? 'text-[hsl(var(--success))]' : 'text-muted-foreground')}>
                                  {delta > 0 ? '+' : ''}{money(delta)}
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>

                    {/* Sequential action bar — only the current stage's group (or Admin) can act */}
                    {rev.status === 'Pending' && rev.canActNow && (
                      <div className="mt-3 flex flex-wrap items-center gap-2 border-t pt-3" data-testid={`rev-actions-${rev.revNo}`}>
                        <span className="flex items-center gap-1.5 text-xs text-muted-foreground">
                          {rev.isAdminOverride
                            ? <><ShieldCheck size={13} className="text-[hsl(var(--brand))]" />Admin override — deciding for {rev.currentStage?.label}</>
                            : <><Clock size={13} className="text-[hsl(var(--warning))]" />Awaiting your decision as {rev.currentStage?.label}</>}
                        </span>
                        <div className="ml-auto flex flex-wrap gap-2">
                          <Button size="sm" variant="outline" data-testid={`rev-reject-${rev.revNo}`}
                            className="text-[hsl(var(--danger))] hover:text-[hsl(var(--danger))]"
                            onClick={() => decideRevision(rev, 'Rejected')}>
                            <X size={14} className="mr-1.5" />Reject
                          </Button>
                          <Button size="sm" data-testid={`rev-approve-${rev.revNo}`} onClick={() => decideRevision(rev, 'Approved')}>
                            <Check size={14} className="mr-1.5" />Approve
                          </Button>
                          {isAdmin && (
                            <Button size="sm" variant="secondary" data-testid={`rev-approve-all-${rev.revNo}`}
                              onClick={() => decideRevision(rev, 'Approved', true)}>
                              <ShieldCheck size={14} className="mr-1.5" />Approve all stages
                            </Button>
                          )}
                        </div>
                      </div>
                    )}
                    {rev.status === 'Pending' && !rev.canActNow && rev.currentStage && (
                      <p className="mt-3 border-t pt-3 text-xs text-muted-foreground" data-testid={`rev-waiting-${rev.revNo}`}>
                        <Clock size={12} className="mr-1 inline" />Pending {rev.currentStage.label}.
                      </p>
                    )}
                    {rev.status !== 'Pending' && (
                      <p className="mt-3 border-t pt-3 text-xs text-muted-foreground">
                        {rev.status === 'Approved' ? 'Fully approved and now effective' : 'Rejected'} · {fmtDate(rev.decidedAt)}
                      </p>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </TabsContent>

        {/* C11 — Budget Savings Capture (ring-fenced under-budget surpluses) */}
        <TabsContent value="savings" className="mt-4 space-y-4" data-testid="savings-tab">
          <div className="flex flex-wrap items-end justify-between gap-3">
            <div>
              <h3 className="font-display text-base font-semibold">Budget Savings</h3>
              <p className="max-w-2xl text-xs text-muted-foreground">Costing banks an under-budget surplus (from a quantity reduction or a rate negotiation) as a saving. The amount is carved OUT of spendable budget and ring-fenced so the project team can&apos;t reallocate it. Only Admin can release it back. Total budget = Booked + Spendable Balance + Savings.</p>
            </div>
          </div>

          {/* Savings summary */}
          <div className="grid gap-3 sm:grid-cols-3">
            <div className="erak-card flex items-center gap-3 p-4" data-testid="savings-total-banked">
              <span className="grid h-10 w-10 shrink-0 place-items-center rounded-xl bg-[hsl(var(--copper)/0.12)] text-[hsl(var(--copper))]"><PiggyBank size={18} /></span>
              <div><div className="text-[10px] uppercase tracking-wide text-muted-foreground">Total Savings (banked)</div>
                <div className="money text-lg font-bold text-[hsl(var(--copper))]">{money(totals.saved || 0)}</div></div>
            </div>
            <div className="erak-card flex items-center gap-3 p-4">
              <span className="grid h-10 w-10 shrink-0 place-items-center rounded-xl bg-[hsl(var(--success)/0.12)] text-[hsl(var(--success))]"><TrendingDown size={18} /></span>
              <div><div className="text-[10px] uppercase tracking-wide text-muted-foreground">Spendable Balance</div>
                <div className={cn('money text-lg font-bold', totals.overBudget && 'text-[hsl(var(--danger))]')}>{money(totals.balance)}</div></div>
            </div>
            <div className="erak-card flex items-center gap-3 p-4">
              <span className="grid h-10 w-10 shrink-0 place-items-center rounded-xl bg-[hsl(var(--brand)/0.12)] text-[hsl(var(--brand))]"><Wallet size={18} /></span>
              <div><div className="text-[10px] uppercase tracking-wide text-muted-foreground">Total Budget</div>
                <div className="money text-lg font-bold">{money(totals.budget)}</div></div>
            </div>
          </div>

          {savingsData === null ? (
            <div className="h-40 animate-pulse rounded-2xl bg-muted/40" />
          ) : (savingsData.savings || []).length === 0 ? (
            <div className="erak-card grid place-items-center gap-2 p-10 text-center" data-testid="savings-empty">
              <PiggyBank size={28} className="text-muted-foreground/60" />
              <div className="text-sm font-medium">No savings banked yet</div>
              <p className="max-w-sm text-xs text-muted-foreground">When a line comes in under budget, Costing can mark the surplus as saved from the Budget Tracker (the “Save” action on a line) to ring-fence it here.</p>
            </div>
          ) : (
            <div className="erak-card overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full min-w-[820px] text-sm" data-testid="savings-table">
                  <thead>
                    <tr className="border-b bg-muted/40 text-left text-[11px] uppercase tracking-wide text-muted-foreground">
                      <th className="px-3 py-2.5 font-semibold">Line</th>
                      <th className="px-3 py-2.5 font-semibold">Type</th>
                      <th className="px-3 py-2.5 text-right font-semibold">Amount</th>
                      <th className="px-3 py-2.5 font-semibold">Marked by</th>
                      <th className="px-3 py-2.5 font-semibold">Status</th>
                      <th className="px-3 py-2.5" />
                    </tr>
                  </thead>
                  <tbody>
                    {savingsData.savings.map((s) => {
                      const released = s.status === 'Released';
                      const typeLabel = { qty: 'Qty', rate: 'Rate', both: 'Qty + Rate' }[s.savingType] || s.savingType;
                      return (
                        <tr key={s.id} data-testid={`saving-row-${s.id}`} className={cn('border-b last:border-0 hover:bg-muted/30', released && 'opacity-60')}>
                          <td className="px-3 py-2.5">
                            <div className="flex flex-wrap items-center gap-1.5">
                              {s.code && <span className="font-num text-xs text-[hsl(var(--copper))]">{s.code}</span>}
                              <span>{s.description}</span>
                              {s.itemNo && <Badge variant="outline" className="text-[10px]">{s.itemNo}</Badge>}
                            </div>
                            {s.reason && <div className="mt-0.5 text-[11px] text-muted-foreground">{s.reason}</div>}
                          </td>
                          <td className="px-3 py-2.5"><Badge variant="secondary" className="text-[10px]">{typeLabel}</Badge></td>
                          <td className="px-3 py-2.5 text-right money font-semibold text-[hsl(var(--copper))]">{money(s.amount)}</td>
                          <td className="px-3 py-2.5 text-xs">
                            <div>{s.markedBy}</div>
                            <div className="text-muted-foreground">{fmtDate(s.markedAt)}</div>
                          </td>
                          <td className="px-3 py-2.5">
                            {released ? (
                              <Badge variant="outline" className="gap-1 text-[10px] text-[hsl(var(--success))]" data-testid={`saving-status-${s.id}`}><Unlock size={10} />Released</Badge>
                            ) : (
                              <Badge variant="outline" className="gap-1 text-[10px] text-[hsl(var(--copper))]" data-testid={`saving-status-${s.id}`}><Lock size={10} />Ring-fenced</Badge>
                            )}
                            {released && <div className="mt-0.5 text-[10px] text-muted-foreground">by {s.releasedBy} · {fmtDate(s.releasedAt)}</div>}
                          </td>
                          <td className="px-3 py-2.5 text-right">
                            {!released && canReleaseSaving && (
                              <Button size="sm" variant="outline" className="h-7" data-testid={`release-saving-${s.id}`} onClick={() => openRelease(s)}>
                                <Unlock size={13} className="mr-1" />Release
                              </Button>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}
          {!canMarkSaving && <p className="text-[11px] text-muted-foreground">You have view-only access to savings. Only Costing (Booking/Commercial) can mark savings and only Admin can release them.</p>}
        </TabsContent>


        {/* Activity History (C4) */}
        <TabsContent value="history" className="mt-4">
          <div className="mb-3">
            <h3 className="font-display text-base font-semibold">Activity History</h3>
            <p className="text-xs text-muted-foreground">Chronological log of bookings, revisions, execution and team changes. Bookings show the point-in-time running balance.</p>
          </div>
          {history === null ? (
            <div className="space-y-3">{[0, 1, 2].map((i) => <div key={i} className="h-16 animate-pulse rounded-xl bg-muted/40" />)}</div>
          ) : (history.events || []).length === 0 ? (
            <div className="erak-card grid place-items-center gap-2 p-10 text-center" data-testid="history-empty">
              <History size={28} className="text-muted-foreground/60" />
              <div className="text-sm font-medium">No activity yet</div>
              <p className="max-w-sm text-xs text-muted-foreground">Bookings and revisions on this project will appear here as a timeline.</p>
            </div>
          ) : (
            <ol className="relative space-y-3 border-l border-border pl-6" data-testid="history-timeline">
              {history.events.map((ev, i) => {
                const meta = EVENT_META[ev.kind] || EVENT_META.status;
                const Icon = meta.icon;
                return (
                  <li key={`${ev.at ?? ''}-${ev.kind ?? ''}-${i}`} data-testid={`history-event-${i}`} className="relative">
                    <span className={cn('absolute -left-[34px] grid h-7 w-7 place-items-center rounded-full ring-4 ring-background',
                      `bg-[hsl(var(--${meta.tone})/0.15)] text-[hsl(var(--${meta.tone}))]`)}>
                      <Icon size={14} />
                    </span>
                    <div className="erak-card p-3">
                      {ev.kind === 'booking' ? (
                        <div className="flex flex-wrap items-start justify-between gap-2">
                          <div className="min-w-0">
                            <div className="flex flex-wrap items-center gap-2 text-sm">
                              <span className="font-medium">{meta.label}</span>
                              {ev.lineCode && <span className="font-num text-xs text-[hsl(var(--copper))]">{ev.lineCode}</span>}
                              <span className="text-muted-foreground">{ev.lineDescription}</span>
                              {ev.itemNo && <Badge variant="outline" className="text-[10px]">{ev.itemNo}</Badge>}
                            </div>
                            <div className="mt-1 text-[11px] text-muted-foreground">
                              {ev.qty != null && ev.rate != null && (
                                <span className="font-num text-foreground">{fmtQty(ev.qty)}{ev.unit ? ` ${ev.unit}` : ''} × {money(ev.rate)}<span className="text-muted-foreground"> · </span></span>
                              )}
                              {ev.actor} · {fmtDate(ev.at)}{ev.reference ? ` · Ref ${ev.reference}` : ''}{ev.note ? ` · ${ev.note}` : ''}
                            </div>
                          </div>
                          <div className="text-right">
                            <div className="money font-bold text-[hsl(var(--teal))]">+{money(ev.amount)}</div>
                            <div className="text-[10px] text-muted-foreground">Booked {money(ev.runningBooked)}</div>
                            <div className={cn('text-[10px]', ev.overBudget ? 'text-[hsl(var(--danger))]' : 'text-muted-foreground')}>Balance {money(ev.runningBalance)}</div>
                          </div>
                        </div>
                      ) : (
                        <div className="flex flex-wrap items-center justify-between gap-2">
                          <div className="min-w-0">
                            <div className="text-sm">{ev.message}</div>
                            <div className="mt-0.5 text-[11px] text-muted-foreground">{ev.actor} · {fmtDate(ev.at)}</div>
                          </div>
                          <Badge variant="outline" className="text-[10px] capitalize">{meta.label}</Badge>
                        </div>
                      )}
                    </div>
                  </li>
                );
              })}
            </ol>
          )}
        </TabsContent>

        {/* Baseline Cost BOQ (read-only, Qty × Rate, collapsible — C7) */}
        <TabsContent value="boq" className="mt-4 space-y-4">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div className="flex-1 rounded-xl border border-[hsl(var(--brand)/0.3)] bg-[hsl(var(--brand)/0.06)] p-3 text-xs text-muted-foreground">
              <Lock size={12} className="mr-1 inline" />This is the immutable baseline captured when the tender was Won. It cannot be edited.
            </div>
            <Button variant="ghost" size="sm" className="h-8 text-xs" data-testid="boq-collapse-all" onClick={toggleBoqAll}>
              {boqAllOpen ? <ChevronsDownUp size={14} className="mr-1.5" /> : <ChevronsUpDown size={14} className="mr-1.5" />}
              {boqAllOpen ? 'Collapse all' : 'Expand all'}
            </Button>
          </div>
          {(computed?.saleItems || []).map((si) => (
            <div key={si.itemNo} className="erak-card overflow-hidden">
              <div className="border-b bg-muted/30 px-4 py-2.5 text-sm font-semibold">Item {si.itemNo} · {si.description}</div>
              <div className="hidden overflow-x-auto md:block"><table className="w-full min-w-[560px] text-sm">
                <thead><tr className="border-b text-left text-[10px] uppercase tracking-wide text-muted-foreground">
                  <th className="px-4 py-2 font-semibold">Category / Line</th>
                  <th className="px-4 py-2 text-right font-semibold">Qty</th>
                  <th className="px-4 py-2 text-right font-semibold">Rate</th>
                  <th className="px-4 py-2 text-right font-semibold">Amount</th>
                </tr></thead>
                <tbody>
                  {si.costBoq.categories.map((cat) => {
                    const key = `${si.itemNo}::${cat.name}`;
                    const open = !!boqExpanded[key];
                    const subtotal = cat.lines.reduce((s, ln) => s + (ln.amount || 0), 0);
                    return (
                      <React.Fragment key={cat.name}>
                        <tr data-testid={`boq-cat-${si.itemNo}-${cat.name}`} onClick={() => toggleBoqCat(key)}
                          className="cursor-pointer bg-muted/20 text-xs font-semibold uppercase text-muted-foreground hover:bg-muted/40">
                          <td className="px-4 py-1.5">
                            <span className="flex items-center gap-1.5">
                              <ChevronRight size={13} className={cn('transition-transform', open && 'rotate-90')} />
                              {cat.name}
                              <span className="ml-1 rounded-full bg-muted px-1.5 text-[10px] font-medium normal-case">{cat.lines.length}</span>
                            </span>
                          </td>
                          <td /><td />
                          <td className="px-4 py-1.5 text-right money normal-case">{money(subtotal)}</td>
                        </tr>
                        {open && cat.lines.map((ln) => (
                          <tr key={ln.lineId} className="border-b last:border-0">
                            <td className="px-4 py-2 pl-9">{ln.code && <span className="font-num text-xs text-[hsl(var(--copper))]">{ln.code} </span>}{ln.description}{ln.brandName ? ` · ${ln.brandName}` : ''}</td>
                            <td className="px-4 py-2 text-right font-num tabular-nums">{fmtQty(ln.qtyPerUnit)}{ln.unit ? <span className="text-[10px] text-muted-foreground"> {ln.unit}</span> : ''}</td>
                            <td className="px-4 py-2 text-right money text-muted-foreground">{money(ln.rate)}</td>
                            <td className="px-4 py-2 text-right money">{money(ln.amount)}</td>
                          </tr>
                        ))}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table></div>

              {/* Mobile: stacked cards (no side-scrolling) */}
              <div className="divide-y md:hidden" data-testid={`boq-cards-${si.itemNo}`}>
                {si.costBoq.categories.map((cat) => {
                  const key = `${si.itemNo}::${cat.name}`;
                  const open = !!boqExpanded[key];
                  const subtotal = cat.lines.reduce((s, ln) => s + (ln.amount || 0), 0);
                  return (
                    <div key={cat.name}>
                      <button type="button" data-testid={`boq-card-cat-${si.itemNo}-${cat.name}`} onClick={() => toggleBoqCat(key)}
                        className="flex w-full items-center gap-2 bg-muted/20 px-4 py-2.5 text-left active:bg-muted/40">
                        <ChevronRight size={14} className={cn('shrink-0 transition-transform', open && 'rotate-90')} />
                        <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">{cat.name}</span>
                        <span className="rounded-full bg-muted px-1.5 text-[10px] font-medium text-muted-foreground">{cat.lines.length}</span>
                        <span className="ml-auto money text-sm font-semibold">{money(subtotal)}</span>
                      </button>
                      {open && cat.lines.map((ln) => (
                        <div key={ln.lineId} className="border-t px-4 py-3" data-testid={`boq-card-line-${ln.lineId}`}>
                          <div className="mb-2 text-sm">
                            {ln.code && <span className="font-num text-xs text-[hsl(var(--copper))]">{ln.code} </span>}
                            {ln.description}{ln.brandName ? ` · ${ln.brandName}` : ''}
                          </div>
                          <div className="space-y-1 text-sm">
                            <div className="flex items-center justify-between gap-3">
                              <span className="text-[11px] uppercase tracking-wide text-muted-foreground">Qty</span>
                              <span className="font-num tabular-nums">{fmtQty(ln.qtyPerUnit)}{ln.unit ? <span className="text-[10px] text-muted-foreground"> {ln.unit}</span> : ''}</span>
                            </div>
                            <div className="flex items-center justify-between gap-3">
                              <span className="text-[11px] uppercase tracking-wide text-muted-foreground">Rate</span>
                              <span className="money text-muted-foreground">{money(ln.rate)}</span>
                            </div>
                            <div className="flex items-center justify-between gap-3 border-t pt-1">
                              <span className="text-[11px] uppercase tracking-wide text-muted-foreground">Amount</span>
                              <span className="money font-semibold">{money(ln.amount)}</span>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </TabsContent>
        </div>
      </Tabs>

      {/* Booking dialog (C6: Qty only @ fixed rate) */}
      <Dialog open={!!bookDialog} onOpenChange={(o) => !o && setBookDialog(null)}>
        <DialogContent data-testid="booking-dialog">
          <DialogHeader><DialogTitle>Book by quantity</DialogTitle></DialogHeader>
          {bookDialog && (() => {
            const qtyN = Number(bookForm.qty) || 0;
            const budgetRateFils = bookDialog.rate || 0;
            const rateFils = Math.round((Number(bookForm.rate) || 0) * 100);
            const amountFils = Math.round(qtyN * rateFils);
            const remaining = bookDialog.balanceQty ?? 0;
            const over = qtyN - remaining > 1e-6;
            const overRate = rateFils > budgetRateFils;
            const underRate = rateFils > 0 && rateFils < budgetRateFils;
            const savingPerLine = underRate ? Math.round(qtyN * (budgetRateFils - rateFils)) : 0;
            return (
              <div className="space-y-3">
                <div className="rounded-lg border bg-muted/30 p-2.5 text-xs">
                  <div className="font-medium">{bookDialog.code ? `${bookDialog.code} · ` : ''}{bookDialog.description}</div>
                  <div className="mt-1 grid grid-cols-3 gap-2 text-muted-foreground">
                    <span>Budget qty: <span className="font-num text-foreground">{fmtInt(bookDialog.budgetQty)} {bookDialog.unit || ''}</span></span>
                    <span>Booked: <span className="font-num text-foreground">{fmtInt(bookDialog.bookedQty)}</span></span>
                    <span>Remaining: <span className="font-num text-foreground" data-testid="booking-remaining">{fmtInt(remaining)}</span></span>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1.5"><Label>Quantity to book{bookDialog.unit ? ` (${bookDialog.unit})` : ''}</Label>
                    <NumberStepper value={bookForm.qty} step={1} decimals={0} min={1} size="md" className="w-full" data-testid="booking-qty"
                      onChange={(v) => setBookForm({ ...bookForm, qty: String(v) })} />
                    <p className="text-[10px] text-muted-foreground">Whole numbers only.</p></div>
                  <div className="space-y-1.5"><Label>Actual rate ({cur})</Label>
                    <NumberStepper value={bookForm.rate} step={1} decimals={2} min={0} size="md" data-testid="booking-rate"
                      className={cn('w-full', overRate && 'border-[hsl(var(--danger))]')}
                      onChange={(v) => setBookForm({ ...bookForm, rate: String(v) })} />
                    <p className="text-[10px] text-muted-foreground">Budgeted rate (max): <span className="font-num">{money(budgetRateFils)}</span>. Cheaper is fine.</p></div>
                </div>
                <div className="flex items-center justify-between rounded-lg border bg-muted/20 px-3 py-2">
                  <span className="text-xs text-muted-foreground">Amount (Qty × Rate)</span>
                  <span className="money text-base font-bold" data-testid="booking-amount-preview">{money(amountFils)}</span>
                </div>
                {overRate && (
                  <div data-testid="booking-over-rate" className="flex items-center gap-1.5 rounded-lg bg-[hsl(var(--danger)/0.1)] px-3 py-2 text-xs text-[hsl(var(--danger))]">
                    <AlertTriangle size={13} />
                    Rate exceeds the budgeted rate ({money(budgetRateFils)}). To book above it, raise a rate revision first.
                  </div>
                )}
                {underRate && !overRate && (
                  <div data-testid="booking-under-rate" className="flex items-center gap-1.5 rounded-lg bg-[hsl(var(--copper)/0.1)] px-3 py-2 text-xs text-[hsl(var(--copper))]">
                    <PiggyBank size={13} />
                    {money(savingPerLine)} under budget on this booking — bankable as a saving once booked.
                  </div>
                )}
                {over && (
                  <div data-testid="booking-over-qty" className="flex items-center gap-1.5 rounded-lg bg-[hsl(var(--danger)/0.1)] px-3 py-2 text-xs text-[hsl(var(--danger))]">
                    <AlertTriangle size={13} />
                    Exceeds remaining quantity ({fmtInt(remaining)} {bookDialog.unit || ''}). Raise a budget revision to increase qty.
                  </div>
                )}
                <div className="space-y-1.5"><Label>Reference (PO / subcontract)</Label>
                  <Input data-testid="booking-reference" value={bookForm.reference} onChange={(e) => setBookForm({ ...bookForm, reference: e.target.value })} placeholder="e.g. PO-1042" /></div>
                <div className="space-y-1.5"><Label>Note</Label>
                  <Textarea rows={2} value={bookForm.note} onChange={(e) => setBookForm({ ...bookForm, note: e.target.value })} placeholder="Optional" /></div>
              </div>
            );
          })()}
          <DialogFooter>
            <Button variant="outline" onClick={() => setBookDialog(null)}>Cancel</Button>
            <Button data-testid="booking-submit"
              disabled={!bookDialog || !(Number(bookForm.qty) > 0) || (Number(bookForm.qty) - (bookDialog?.balanceQty ?? 0) > 1e-6)
                || !(Number(bookForm.rate) > 0) || (Math.round((Number(bookForm.rate) || 0) * 100) > (bookDialog?.rate || 0))}
              onClick={submitBooking}>Record booking</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Team assignment dialog */}
      <Dialog open={teamOpen} onOpenChange={setTeamOpen}>
        <DialogContent data-testid="team-dialog" className="max-h-[85vh] overflow-y-auto sm:max-w-md">
          <DialogHeader><DialogTitle>Assign Project Team</DialogTitle></DialogHeader>
          <p className="text-xs text-muted-foreground">Link existing users to this project. Users are created and grouped in User Management — this only assigns them here.</p>
          <div className="space-y-1.5"><Label>Project Manager</Label>
            <Select value={teamForm.projectManagerId || '__none'} onValueChange={(v) => setTeamForm({ ...teamForm, projectManagerId: v === '__none' ? '' : v })}>
              <SelectTrigger data-testid="team-pm-select"><SelectValue placeholder="Select PM" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__none">Unassigned</SelectItem>
                {candidates.pms.map((u) => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}
              </SelectContent>
            </Select>
            {candidates.pms.length === 0 && <p className="text-[11px] text-muted-foreground">No users in the Project Manager group yet.</p>}
          </div>
          <div className="space-y-1.5"><Label>Assigned Engineers</Label>
            <div className="max-h-52 space-y-1 overflow-y-auto rounded-xl border p-2">
              {candidates.engineers.length === 0 && <p className="px-1 py-2 text-[11px] text-muted-foreground">No users in the Project Engineer group yet.</p>}
              {candidates.engineers.map((u) => (
                <label key={u.id} data-testid={`team-engineer-${u.id}`} className="flex cursor-pointer items-center gap-2 rounded-lg px-2 py-1.5 hover:bg-muted">
                  <Checkbox checked={teamForm.assignedEngineerIds.includes(u.id)} onCheckedChange={() => toggleEngineer(u.id)} />
                  <span className="text-sm">{u.name}</span>
                </label>
              ))}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setTeamOpen(false)}>Cancel</Button>
            <Button data-testid="team-submit" onClick={submitTeam}>Save team</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* C11 — Mark saving dialog (Costing / Admin) */}
      <Dialog open={!!saveDialog} onOpenChange={(o) => !o && setSaveDialog(null)}>
        <DialogContent data-testid="saving-dialog" className="sm:max-w-md">
          <DialogHeader><DialogTitle>Mark surplus as saving</DialogTitle></DialogHeader>
          {saveDialog && (
            <>
              <div className="rounded-xl border bg-muted/20 p-3 text-xs">
                <div className="flex items-center gap-2">
                  {saveDialog.code && <span className="font-num text-[hsl(var(--copper))]">{saveDialog.code}</span>}
                  <span className="font-medium">{saveDialog.description}</span>
                </div>
                <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-muted-foreground">
                  <span>Budget <span className="money text-foreground">{money(saveDialog.workingBudget)}</span></span>
                  <span>Booked <span className="money text-foreground">{money(saveDialog.booked)}</span></span>
                  <span>Available surplus <span className="money font-semibold text-[hsl(var(--copper))]" data-testid="saving-max">{money(saveDialog.balance)}</span></span>
                </div>
              </div>
              <p className="text-[11px] text-muted-foreground">The saved amount is carved out of spendable budget and ring-fenced. It cannot exceed the available surplus and will reduce how much can still be booked on this line.</p>

              <div className="grid gap-3">
                <div className="space-y-1.5">
                  <Label>Saving type</Label>
                  <Select value={saveForm.savingType} onValueChange={(v) => setSaveForm({ ...saveForm, savingType: v })}>
                    <SelectTrigger data-testid="saving-type"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="qty">Quantity reduction</SelectItem>
                      <SelectItem value="rate">Rate negotiation</SelectItem>
                      <SelectItem value="both">Quantity + Rate</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5">
                  <Label>Amount to save ({cur})</Label>
                  <NumberStepper value={saveForm.amount} step={1} decimals={2} min={0} max={saveDialog.balance / 100} size="md" className="w-full" data-testid="saving-amount"
                    onChange={(v) => setSaveForm({ ...saveForm, amount: String(v) })} />
                  <button type="button" className="text-[11px] text-[hsl(var(--brand))] hover:underline" data-testid="saving-use-max"
                    onClick={() => setSaveForm({ ...saveForm, amount: (saveDialog.balance / 100).toFixed(2) })}>
                    Use full surplus ({money(saveDialog.balance)})
                  </button>
                </div>
                <div className="space-y-1.5">
                  <Label>Reason</Label>
                  <Textarea rows={2} data-testid="saving-reason" value={saveForm.reason}
                    onChange={(e) => setSaveForm({ ...saveForm, reason: e.target.value })}
                    placeholder="e.g. Negotiated transformer rate down 12%; banking the surplus" />
                </div>
              </div>
            </>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setSaveDialog(null)}>Cancel</Button>
            <Button data-testid="saving-submit" disabled={!(Number(saveForm.amount) > 0)} onClick={submitSaving}>
              <PiggyBank size={15} className="mr-2" />Bank saving
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* C11 — Release saving dialog (Admin-only) */}
      <Dialog open={!!releaseDialog} onOpenChange={(o) => !o && setReleaseDialog(null)}>
        <DialogContent data-testid="release-dialog" className="sm:max-w-md">
          <DialogHeader><DialogTitle>Release saving to spendable budget</DialogTitle></DialogHeader>
          {releaseDialog && (
            <>
              <p className="text-xs text-muted-foreground">
                Releasing <span className="money font-semibold text-[hsl(var(--copper))]">{money(releaseDialog.amount)}</span> on
                {releaseDialog.code ? <span className="font-num text-[hsl(var(--copper))]"> {releaseDialog.code}</span> : ''} returns it to the project&apos;s spendable budget so it can be booked again. This is audited.
              </p>
              <div className="space-y-1.5">
                <Label>Reason for release</Label>
                <Textarea rows={2} data-testid="release-reason" value={releaseReason}
                  onChange={(e) => setReleaseReason(e.target.value)}
                  placeholder="e.g. Surplus needed to cover an approved scope addition on civil works" />
              </div>
            </>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setReleaseDialog(null)}>Cancel</Button>
            <Button data-testid="release-submit" onClick={submitRelease}>
              <Unlock size={15} className="mr-2" />Release saving
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>


      {/* Duration dialog (C5, Admin-only) */}
      <Dialog open={durOpen} onOpenChange={setDurOpen}>
        <DialogContent data-testid="duration-dialog" className="sm:max-w-md">
          <DialogHeader><DialogTitle>Edit project duration</DialogTitle></DialogHeader>
          <p className="text-xs text-muted-foreground">Start and end dates were fixed at award. Only Admin can adjust them here.</p>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5"><Label>Start date</Label>
              <Input type="date" data-testid="duration-start" value={durForm.startDate} onChange={(e) => setDurForm({ ...durForm, startDate: e.target.value })} /></div>
            <div className="space-y-1.5"><Label>End date</Label>
              <Input type="date" data-testid="duration-end" value={durForm.endDate} onChange={(e) => setDurForm({ ...durForm, endDate: e.target.value })} /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDurOpen(false)}>Cancel</Button>
            <Button data-testid="duration-submit" onClick={submitDuration}>Save duration</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Raise Revision dialog (C9) */}
      <Dialog open={revOpen} onOpenChange={setRevOpen}>
        <DialogContent data-testid="revision-dialog" className="max-h-[88vh] overflow-y-auto sm:max-w-2xl">
          <DialogHeader><DialogTitle>Raise budget revision</DialogTitle></DialogHeader>
          <p className="text-xs text-muted-foreground">Propose a new quantity and/or rate on one or more lines. The revision is routed for sequential approval and only changes the effective budget once fully approved.</p>

          <div className="space-y-1.5">
            <Label>Reason for revision</Label>
            <Textarea rows={2} data-testid="revision-reason" value={revForm.reason}
              onChange={(e) => setRevForm({ ...revForm, reason: e.target.value })}
              placeholder="e.g. Scope increase on civil works per site instruction SI-12" />
          </div>

          {/* Line picker */}
          <div className="rounded-xl border p-3">
            <div className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">Add a line change</div>
            <div className="mt-2 grid gap-2 sm:grid-cols-[1fr,auto,auto,auto]">
              <Select value={revPick.lineId} onValueChange={(v) => {
                const bl = (budget?.lines || []).find((l) => l.lineId === v);
                setRevPick({ lineId: v, newQty: bl ? String(bl.budgetQty) : '', newRate: bl ? String(bl.rate / 100) : '' });
              }}>
                <SelectTrigger data-testid="revision-line-select"><SelectValue placeholder="Select a line" /></SelectTrigger>
                <SelectContent>
                  {(budget?.lines || []).map((l) => (
                    <SelectItem key={l.lineId} value={l.lineId}>
                      {l.code ? `${l.code} · ` : ''}{l.description}{l.itemNo ? ` (${l.itemNo})` : ''}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <div className="space-y-1">
                <Label className="text-[10px] text-muted-foreground">New qty</Label>
                <NumberStepper value={revPick.newQty} step={1} decimals={0} min={1} size="md" data-testid="revision-new-qty" inputClassName="w-14"
                  onChange={(v) => setRevPick({ ...revPick, newQty: String(v) })} />
              </div>
              <div className="space-y-1">
                <Label className="text-[10px] text-muted-foreground">New rate ({cur})</Label>
                <NumberStepper value={revPick.newRate} step={1} decimals={2} min={0} size="md" data-testid="revision-new-rate" inputClassName="w-20"
                  onChange={(v) => setRevPick({ ...revPick, newRate: String(v) })} />
              </div>
              <div className="flex items-end">
                <Button type="button" variant="outline" size="sm" className="h-9" data-testid="revision-add-line"
                  disabled={!revPick.lineId} onClick={addRevLine}>
                  <Plus size={14} className="mr-1" />Add
                </Button>
              </div>
            </div>
          </div>

          {/* Staged changes */}
          {revForm.lines.length === 0 ? (
            <p className="rounded-lg border border-dashed px-3 py-4 text-center text-xs text-muted-foreground" data-testid="revision-no-lines">
              No line changes added yet.
            </p>
          ) : (
            <div className="overflow-x-auto rounded-xl border">
              <table className="w-full min-w-[480px] text-sm">
                <thead><tr className="border-b bg-muted/40 text-left text-[10px] uppercase tracking-wide text-muted-foreground">
                  <th className="px-3 py-2 font-semibold">Line</th>
                  <th className="px-3 py-2 text-right font-semibold">New Qty</th>
                  <th className="px-3 py-2 text-right font-semibold">New Rate</th>
                  <th className="px-3 py-2" />
                </tr></thead>
                <tbody>
                  {revForm.lines.map((ln) => {
                    const bl = (budget?.lines || []).find((l) => l.lineId === ln.lineId);
                    return (
                      <tr key={ln.lineId} className="border-b last:border-0" data-testid={`revision-staged-${ln.lineId}`}>
                        <td className="px-3 py-2">{bl?.code ? `${bl.code} · ` : ''}{bl?.description || ln.lineId}</td>
                        <td className="px-3 py-2 text-right font-num tabular-nums">{fmtInt(ln.newQty)}</td>
                        <td className="px-3 py-2 text-right money">{money(Math.round(ln.newRate * 100))}</td>
                        <td className="px-3 py-2 text-right">
                          <Button type="button" variant="ghost" size="icon" className="h-7 w-7 text-[hsl(var(--danger))]"
                            data-testid={`revision-remove-${ln.lineId}`} onClick={() => removeRevLine(ln.lineId)}>
                            <Trash2 size={14} />
                          </Button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setRevOpen(false)}>Cancel</Button>
            <Button data-testid="revision-submit" disabled={!revForm.reason.trim() || revForm.lines.length === 0} onClick={submitRevision}>
              <GitBranch size={15} className="mr-2" />Raise revision
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
